import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '@/i18n';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import { Plus } from 'lucide-react';
import TemuOpsPager, { TEMU_OPS_DEFAULT_PAGE_SIZE } from '@/temu-platform/components/TemuOpsPager';
import { togglePageSelection } from '@/temu-platform/utils/temuPageSelection';
import BatchQueryModal from '@/temu-platform/modules/products/components/BatchQueryModal';
import {
  BATCH_QUERY_LIMIT,
  joinBatchTokens,
  parseBatchTokens,
} from '@/temu-platform/modules/products/components/batchQueryUtils';
import HoverCopyField from '@/temu-platform/modules/products/components/HoverCopyField';
import TemuProductThumb from '@/temu-platform/modules/products/components/TemuProductThumb';
import TemuImagePreviewLightbox from '@/temu-platform/modules/products/components/TemuImagePreviewLightbox';
import SearchableSelect from '@/temu-platform/modules/products/components/SearchableSelect';
import { ProductFilterCard, ProductListCard } from '@/temu-platform/modules/products/components/ProductPageCards';
import TemuOpsListToolbar, { downloadCsv } from '@/temu-platform/components/TemuOpsListToolbar';
import TemuSyncProgressSlot from '@/temu-platform/components/TemuSyncProgressSlot';
import { useGuardedRefresh } from '@/temu-platform/hooks/useGuardedRefresh';
import { useTemuPageShopScope, stackShopDimensionTabs } from '@/temu-platform/hooks/useTemuPageShopScope';
import SyncPlatformJsonModal from '@/temu-platform/components/SyncPlatformJsonModal';
import { readNoticeSkc } from '@/temu-platform/utils/temuNoticeFocusQuery';
import { useTemuSyncJobs } from '@/temu-platform/context/TemuSyncJobContext';
import { temuPlatformAPI } from '@/temu-platform/api/temuPlatformApi';
import { extractTemuList, pickField } from '@/temu-platform/utils/temuResponse';
import { flattenLeafCategories } from '@/temu-platform/utils/temuCategoryTree';
import { loadProductMetaBySkc } from '@/temu-platform/utils/temuProductMetaBySkc';
import { pickHighestPriorityTemplate } from '@/temu-platform/utils/temuTemplateAutoRule';
import { styleNoFromSupplierCode } from '@/temu-platform/utils/temuStyleNo';
import { formatSiteListSyncedAt } from '@/temu-platform/utils/temuSiteList';
import { showTemuConfirm } from '@/temu-platform/components/TemuConfirmDialog';
import { showToast } from '@/utils/toast';
import ComplianceAutoBindRuleModal from '@/temu-platform/modules/compliance/ComplianceAutoBindRuleModal';
import {
  readComplianceAutoBindErrors,
} from '@/temu-platform/modules/compliance/complianceAutoBindRules';
import {
  maybeRunPhotoAutoBindAfterSync,
} from '@/temu-platform/modules/compliance/complianceAutoBindRunner';
import TemuUploadSourceMenu from '@/temu-platform/components/TemuUploadSourceMenu';
import { buildOpsExportMenu, opsExportLabel } from '@/temu-platform/utils/buildOpsExportMenu';
import LibraryModalContent from '@/pages/ipe/workbench/modals/LibraryModalContent';
import { imageSpaceRecordToUploadFile } from '@/temu-platform/utils/temuImageSpacePick';
import { appendUniqueImageSpaceRows } from '@/pages/ipe/workbench/utils/imageSpacePageFetch';
import { useFixedAspectCropGate } from '@/components/image/useFixedAspectCropGate';
import '@/styles/ipe/workbench/IpeModalUnified.css';

import {
  PHOTO_STATUS,
  PHOTO_STATUS_TABS,
  emptyPhotoDimensionStats,
  normalizePhotoStatus,
  photoStatusDisplay,
} from '@/temu-platform/modules/compliance/complianceStatusEnums';

const REVIEW_FILTER = Object.fromEntries(
  PHOTO_STATUS_TABS.map((tab) => [tab.id, { label: tab.label }]),
);

const PHOTO_ID_TYPE_OPTIONS = [
  { value: 'spu', label: 'SPU' },
  { value: 'skc', label: 'SKC' },
  { value: 'title', labelKey: 'compliance.lbTitle' },
];

/** skc-label-list skcShelfStatusList：0-待上架；1-已上架；2-已下架；3-售罄 */
const SHELF_OPTIONS = [
  { value: 'all', labelKey: 'tiktokCatalog.all' },
  { value: '1', labelKey: 'compliance.lbShelf1' },
  { value: '2', labelKey: 'compliance.lbShelf2' },
  { value: '0', labelKey: 'compliance.lbShelf0' },
  { value: '3', labelKey: 'compliance.lbShelf3' },
];

const SHELF_LABELS = {
  0: '待上架',
  1: '已上架',
  2: '已下架',
  3: '售罄',
};

const LABEL_GROUP = {
  1: '本体',
  2: '包装',
};

/** 出参 reviewStatus：0 待提交；1 审核中；2 通过；3 不通过 */
const REVIEW_OUT_LABELS = {
  0: '待提交',
  1: '审核中',
  2: '通过',
  3: '不通过',
};

function normalizeLabelRows(res) {
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.info)) return res.info;
  if (Array.isArray(res?.data)) return res.data;
  return extractTemuList(res);
}

function shelfCodesOf(row) {
  const raw = pickField(row, 'skcShelfStatus');
  if (raw == null || raw === '') return [];
  if (Array.isArray(raw)) return raw.map((v) => Number(v)).filter((n) => !Number.isNaN(n));
  const n = Number(raw);
  return Number.isNaN(n) ? [] : [n];
}

function shelfLabel(row) {
  const codes = shelfCodesOf(row);
  if (!codes.length) return '—';
  return codes.map((v) => (SHELF_LABELS[v] ? i18n.t('compliance.lbShelf' + v, { ns: 'workbench' }) : v)).join(' / ');
}

function shelfTone(row) {
  const codes = shelfCodesOf(row);
  if (codes.includes(1)) return 'success';
  if (codes.includes(2)) return 'offline';
  if (codes.includes(3)) return 'failed';
  return 'pending';
}

function labelElements(row) {
  return Array.isArray(row?.skcLabelInfoList) ? row.skcLabelInfoList : [];
}

function labelContentItems(row) {
  const list = labelElements(row);
  if (!list.length) {
    const urls = Array.isArray(row?.pictureUrls) ? row.pictureUrls : [];
    if (urls.length) return [i18n.t('compliance.lbPhotoItems', { ns: 'workbench', count: urls.length })];
    return ['—'];
  }
  return list.map((el) => {
    const name = el.labelName || el.labelId || i18n.t('compliance.lbElement', { ns: 'workbench' });
    const group = LABEL_GROUP[Number(el.labelGroup)]
      ? i18n.t(Number(el.labelGroup) === 1 ? 'compliance.lbBody' : 'compliance.lbPack', { ns: 'workbench' })
      : '';
    return [name, group].filter(Boolean).join('/');
  });
}

/** 合规项目类型：平台未返回监控类型时展示默认文案 */
function complianceProjectTypeItems(row) {
  const raw = pickField(
    row,
    'complianceProjectType',
    'complianceProjectTypeName',
    'monitorTypeName',
    'monitorType',
    'specialMonitorType',
  );
  if (raw != null && String(raw).trim()) {
    return [String(raw).trim()];
  }
  return [i18n.t('compliance.lbNoMonitor', { ns: 'workbench' })];
}

function siteListItems(row) {
  const sites = new Set();
  labelElements(row).forEach((el) => {
    const list = Array.isArray(el?.siteList) ? el.siteList : [];
    list.forEach((s) => {
      const v = String(s || '').trim();
      if (v) sites.add(v);
    });
  });
  if (!sites.size) return ['—'];
  return Array.from(sites);
}

/** 列表「资料状态」：优先 photoStatus；数字码对齐 flash.recognize ruleStatus 1–7 */
function photoMaterialStatusOf(row) {
  const fromCompliance = normalizePhotoStatus(pickField(row, 'photoStatus', 'ruleStatus'));
  if (fromCompliance) return photoStatusDisplay(fromCompliance);
  const status = worstReviewStatus(row);
  if (status == null) return { label: '—', tone: 'pending', code: '' };
  // 兼容旧 1/2/3 审核码；若已是 ruleStatus 1–7，normalizePhotoStatus 可直接识别
  const fromNum = normalizePhotoStatus(status);
  if (fromNum) return photoStatusDisplay(fromNum);
  return photoStatusDisplay(PHOTO_STATUS.WAIT_UPLOAD);
}

function mapReviewFilterToPhotoStatus(reviewFilterKey) {
  return normalizePhotoStatus(reviewFilterKey) || undefined;
}

/** 将 /compliance/photos 行归一为列表行（带 photoStatus） */
function normalizeCompliancePhotoRow(row) {
  const skc = String(pickField(row, 'skc', 'skcName', 'productId') || '').trim();
  const urls = Array.isArray(row?.pictureUrls) ? row.pictureUrls : [];
  const photoStatus = normalizePhotoStatus(pickField(row, 'photoStatus')) || PHOTO_STATUS.WAIT_UPLOAD;
  return {
    ...row,
    skc,
    skcName: skc,
    photoStatus,
    // 无旧版 skcLabelInfoList 时用图片列表兜底实拍图项目类型
    skcLabelInfoList: Array.isArray(row?.skcLabelInfoList) && row.skcLabelInfoList.length
      ? row.skcLabelInfoList
      : (urls.length
        ? urls.map((url, idx) => ({
          labelName: `实拍图${urls.length > 1 ? ` ${idx + 1}` : ''}`,
          labelGroup: 1,
          reviewStatus: photoStatus === PHOTO_STATUS.SUCCESS
            ? 2
            : photoStatus === PHOTO_STATUS.DEEP_RECOGNIZING
              ? 1
              : 0,
          siteList: [],
          previewUrl: typeof url === 'string' ? url : url?.url,
        }))
        : []),
  };
}

function CellStack({ items, title, itemClassName }) {
  const list = Array.isArray(items) && items.length ? items : ['—'];
  return (
    <div className="tpo-cell-stack" title={title || list.join('、')}>
      {list.map((text, idx) => (
        <div key={`${idx}-${text}`} className={itemClassName || 'tpo-cell-stack__item'}>
          {text}
        </div>
      ))}
    </div>
  );
}

function worstReviewStatus(row) {
  const list = labelElements(row);
  if (!list.length) return null;
  const statuses = list.map((el) => Number(el.reviewStatus));
  if (statuses.includes(3)) return 3;
  if (statuses.includes(0)) return 0;
  if (statuses.includes(1)) return 1;
  if (statuses.includes(2)) return 2;
  return statuses[0];
}

/**
 * 列表维度优先用商品合规同步的 photoStatus（/compliance/photos|products），
 * 再回退 skcLabelInfoList.reviewStatus。
 */
function photoDimensionOf(row) {
  const fromCompliance = normalizePhotoStatus(pickField(row, 'photoStatus', 'ruleStatus'));
  if (fromCompliance) return fromCompliance;
  const status = worstReviewStatus(row);
  return normalizePhotoStatus(status) || PHOTO_STATUS.WAIT_UPLOAD;
}

/** 实拍图列表筛选项：审核维度 + 上下架 */
function matchesPhotoListFilters(row, reviewFilterKey, shelfFilterKey) {
  if (shelfFilterKey && shelfFilterKey !== 'all') {
    const code = Number(shelfFilterKey);
    const shelves = shelfCodesOf(row);
    if (!Number.isNaN(code) && shelves.length && !shelves.includes(code)) return false;
  }
  if (reviewFilterKey && reviewFilterKey !== 'all') {
    return photoDimensionOf(row) === reviewFilterKey;
  }
  return true;
}

/** 类目筛选：优先 productTypeId，其次 categoryId */
function metaMatchesCategoryFilter(meta, filter = {}) {
  const typeId = String(filter.productTypeId || '').trim();
  const catId = String(filter.categoryId || '').trim();
  if (!typeId && !catId) return true;
  if (!meta) return false;
  if (typeId && String(meta.productTypeId || '') === typeId) return true;
  if (catId && String(meta.categoryId || '') === catId) return true;
  return false;
}

/** 列表/导出「识别状态」：优先商品合规 photoStatus */
function photoStatusOf(row) {
  return photoMaterialStatusOf(row);
}

/** 已上传：任一元素非「待提交」(0) */
function hasUploadedPhoto(row) {
  if (row?.bindStatus === 'bound') return true;
  return labelElements(row).some((el) => {
    const s = Number(el?.reviewStatus);
    return s === 1 || s === 2 || s === 3;
  });
}

function normalizeReasonList(raw) {
  if (raw == null || raw === '') return [];
  if (Array.isArray(raw)) {
    return raw.map((v) => String(v ?? '').trim()).filter(Boolean);
  }
  const text = String(raw).trim();
  return text ? [text] : [];
}

/** 审核驳回原因：优先元素 failReason，其次 SKC 级 failReasonList */
function collectFailReasons(row) {
  const items = [];
  labelElements(row).forEach((el) => {
    if (Number(el?.reviewStatus) !== 3) return;
    const reasons = normalizeReasonList(el.failReason ?? el.failReasonList);
    const name = el.labelName || el.labelId || i18n.t('compliance.lbRecognize', { ns: 'workbench' });
    if (reasons.length) {
      reasons.forEach((reason) => items.push({ label: name, reason }));
    } else {
      items.push({ label: name, reason: i18n.t('compliance.lbNoReason', { ns: 'workbench' }) });
    }
  });
  if (!items.length) {
    normalizeReasonList(row?.failReasonList ?? row?.failReason).forEach((reason) => {
      items.push({ label: '', reason });
    });
  }
  return items;
}

function hasRejectFailReason(row) {
  return worstReviewStatus(row) === 3 || collectFailReasons(row).length > 0;
}

function collectChannelImages(list, title) {
  const out = [];
  const items = Array.isArray(list) ? list : [];
  items.forEach((item, idx) => {
    const url = pickField(
      item,
      'previewUrl',
      'originalUrl',
      'previewUrlDisplay',
      'originalUrlDisplay',
      'viewUrl',
      'imageUrl',
      'url',
      'pictureUrl',
    );
    if (!url) return;
    out.push({ title: `${title}${items.length > 1 ? ` ${idx + 1}` : ''}`, url });
  });
  return out;
}

function extractUploadedImages(row) {
  const bound = row?.boundPhotos && typeof row.boundPhotos === 'object' ? row.boundPhotos : null;
  if (bound) {
    return [
      ...collectChannelImages(bound.packageImages, i18n.t('compliance.lbPackPhotos', { ns: 'workbench' })),
      ...collectChannelImages(bound.bodyImages, i18n.t('compliance.lbBodyPhotos', { ns: 'workbench' })),
    ];
  }
  const buckets = [
    { key: 'packageLableList', title: i18n.t('compliance.lbPackPhotos', { ns: 'workbench' }) },
    { key: 'packageLabelList', title: i18n.t('compliance.lbPackPhotos', { ns: 'workbench' }) },
    { key: 'bodyLableList', title: i18n.t('compliance.lbBodyPhotos', { ns: 'workbench' }) },
    { key: 'bodyLabelList', title: i18n.t('compliance.lbBodyPhotos', { ns: 'workbench' }) },
  ];
  const out = [];
  buckets.forEach(({ key, title }) => {
    out.push(...collectChannelImages(row?.[key], title));
  });
  return out;
}

const PHOTO_MAX_PER_CHANNEL = 5;
const PHOTO_MAX_BYTES = 10 * 1024 * 1024;
const PHOTO_MAX_EDGE = 8000;
const PHOTO_ACCEPT = '.png,.jpg,.jpeg,image/png,image/jpeg';
function clampPhotoChannel(list = []) {
  return (Array.isArray(list) ? list : []).slice(0, PHOTO_MAX_PER_CHANNEL);
}

function newPhotoItemId() {
  return `photo-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

async function convertPhotoTemplateItem(shopId, item, idx) {
  const originalUrl = String(item?.originalUrl || '').trim();
  const legacyUrl = String(item?.imageUrl || '').trim();
  const legacyMd5 = String(item?.imageMd5 || '').trim();
  if (legacyUrl && legacyMd5) {
    return {
      id: item.id || newPhotoItemId(),
      name: item.name || i18n.t('compliance.lbTplImg', { ns: 'workbench', n: idx + 1 }),
      originalUrl: originalUrl && !originalUrl.startsWith('blob:') ? originalUrl : '',
      previewUrl: item.previewUrl || originalUrl || legacyUrl,
      imageUrl: legacyUrl,
      imageMd5: legacyMd5,
    };
  }
  if (originalUrl && !originalUrl.startsWith('blob:')) {
    const converted = await temuPlatformAPI.convertPhotoTemplateOriginal(shopId, originalUrl);
    const imageUrl = String(converted?.imageUrl || '').trim();
    const imageMd5 = String(converted?.imageMd5 || '').trim();
    if (!imageUrl || !imageMd5) {
      throw new Error(i18n.t('compliance.lbTplFail', { ns: 'workbench', n: idx + 1 }));
    }
    return {
      id: item.id || newPhotoItemId(),
      name: item.name || i18n.t('compliance.lbTplImg', { ns: 'workbench', n: idx + 1 }),
      originalUrl,
      previewUrl: String(item.previewUrl || converted?.previewUrl || originalUrl).trim() || originalUrl,
      imageUrl,
      imageMd5,
    };
  }
  return null;
}

async function convertPhotoTemplateItemList(shopId, list = [], templateId) {
  const source = Array.isArray(list) ? list : [];
  const out = [];
  const fromTemplateId = templateId ? String(templateId) : '';
  for (let i = 0; i < source.length; i += 1) {
    const next = await convertPhotoTemplateItem(shopId, source[i], i);
    if (next) {
      if (fromTemplateId) next.fromTemplateId = fromTemplateId;
      out.push(next);
    }
  }
  return out;
}

async function loadAndConvertPhotoTemplate(shopId, templateId) {
  const res = await temuPlatformAPI.getPhotoTemplate(shopId, templateId);
  const payload = res?.item?.payload || {};
  let pkg = clampPhotoChannel(await convertPhotoTemplateItemList(shopId, payload.packageImages, templateId));
  let body = clampPhotoChannel(await convertPhotoTemplateItemList(shopId, payload.bodyImages, templateId));
  return { pkg, body, item: res?.item || null };
}

function resolvePurePhotoTemplateId(packageItems = [], bodyItems = [], applied = null) {
  const appliedId = String(applied?.id || '').trim();
  if (!appliedId) return null;
  const pkg = Array.isArray(packageItems) ? packageItems : [];
  const body = Array.isArray(bodyItems) ? bodyItems : [];
  const all = [...pkg, ...body];
  if (!all.length) return null;
  if (pkg.length !== Number(applied.packageCount || 0) || body.length !== Number(applied.bodyCount || 0)) {
    return null;
  }
  if (all.some((item) => String(item?.fromTemplateId || '') !== appliedId)) {
    return null;
  }
  const id = Number(appliedId);
  return Number.isFinite(id) && id > 0 ? id : null;
}

/** 将包装/本体图提交到 TEMU bindSkcLabels；失败抛 Error */
async function bindSkcPhotoLabels(shopId, skc, packageItems = [], bodyItems = [], photoTemplateId = null) {
  const pkg = Array.isArray(packageItems) ? packageItems : [];
  const body = Array.isArray(bodyItems) ? bodyItems : [];
  if (!shopId || !skc) {
    throw new Error(i18n.t('compliance.lbMissingShopSkc', { ns: 'workbench' }));
  }
  if (!pkg.length && !body.length) {
    throw new Error(i18n.t('compliance.lbNeedPhoto', { ns: 'workbench' }));
  }
  const payload = { skcList: [skc] };
  if (photoTemplateId) {
    payload.photoTemplateId = photoTemplateId;
  }
  // 平台字段名为 packageLableList / bodyLableList（官方拼写）
  if (pkg.length) {
    payload.packageLableList = pkg.map((item) => ({
      imageUrl: item.imageUrl,
      imageMd5: item.imageMd5,
      originalUrl: item.originalUrl || '',
      previewUrl: item.previewUrl || item.originalUrl || '',
    }));
  }
  if (body.length) {
    payload.bodyLableList = body.map((item) => ({
      imageUrl: item.imageUrl,
      imageMd5: item.imageMd5,
      originalUrl: item.originalUrl || '',
      previewUrl: item.previewUrl || item.originalUrl || '',
    }));
  }
  const result = await temuPlatformAPI.bindSkcLabels(shopId, payload);
  const failed = Number(result?.faildCount || result?.info?.faildCount || 0);
  if (failed > 0) {
    const reason = result?.faildList?.[0]?.reason
      || result?.info?.faildList?.[0]?.reason
      || i18n.t('compliance.lbPartial', { ns: 'workbench' });
    throw new Error(reason);
  }
  return result;
}

function isAllowedPhotoFile(file) {
  if (!file) return false;
  const name = String(file.name || '').toLowerCase();
  const type = String(file.type || '').toLowerCase();
  return (
    type === 'image/png'
    || type === 'image/jpeg'
    || type === 'image/jpg'
    || name.endsWith('.png')
    || name.endsWith('.jpg')
    || name.endsWith('.jpeg')
  );
}

function readImageDimensions(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({ width: img.naturalWidth || img.width, height: img.naturalHeight || img.height });
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error(i18n.t('compliance.lbReadSize', { ns: 'workbench' })));
    };
    img.src = url;
  });
}

async function validatePhotoFile(file) {
  if (!isAllowedPhotoFile(file)) {
    throw new Error(i18n.t('compliance.lbPngOnly', { ns: 'workbench' }));
  }
  if (file.size > PHOTO_MAX_BYTES) {
    throw new Error(i18n.t('compliance.lbTooBig', { ns: 'workbench', size: (file.size / 1024 / 1024).toFixed(1) }));
  }
  const { width, height } = await readImageDimensions(file);
  if (width > PHOTO_MAX_EDGE || height > PHOTO_MAX_EDGE) {
    throw new Error(i18n.t('compliance.lbTooEdge', { ns: 'workbench', max: PHOTO_MAX_EDGE, width, height }));
  }
  if (width < 900 || height < 900) {
    throw new Error(i18n.t('compliance.lbMinEdge', { ns: 'workbench', width, height }));
  }
}

function LabelBindModal({ open, shopId, skc, productMeta = null, onClose, onSaved }) {
  const { t } = useTranslation('workbench');
  const [packageItems, setPackageItems] = useState([]);
  const [bodyItems, setBodyItems] = useState([]);
  const [uploadingChannel, setUploadingChannel] = useState(null);
  const [dragOverChannel, setDragOverChannel] = useState(null);
  const [saving, setSaving] = useState(false);
  const [previewSrc, setPreviewSrc] = useState('');
  const [previewAlt, setPreviewAlt] = useState('');
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [templateName, setTemplateName] = useState('');
  const [templateBusy, setTemplateBusy] = useState(false);
  const packageInputRef = useRef(null);
  const bodyInputRef = useRef(null);
  const packageUploadAnchorRef = useRef(null);
  const bodyUploadAnchorRef = useRef(null);
  const imageSpaceRowsRef = useRef([]);
  const [uploadMenuChannel, setUploadMenuChannel] = useState(null);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [libraryPickChannel, setLibraryPickChannel] = useState(null);
  const [librarySelection, setLibrarySelection] = useState([]);
  const autoAppliedRef = useRef(false);
  const appliedTemplateRef = useRef({ id: '', packageCount: 0, bodyCount: 0 });
  const { ensureAspectFile, cropModal, cropping } = useFixedAspectCropGate({
    title: t('compliance.lbCropTitle'),
    hint: t('compliance.lbCropHint'),
    maxEdge: PHOTO_MAX_EDGE,
    maxBytes: PHOTO_MAX_BYTES,
    minCropEdge: 900,
  });

  useEffect(() => {
    if (!open) return;
    setPackageItems([]);
    setBodyItems([]);
    setUploadingChannel(null);
    setDragOverChannel(null);
    setSaving(false);
    setPreviewSrc('');
    setPreviewAlt('');
    setSelectedTemplateId('');
    setTemplateName('');
    setTemplateBusy(false);
    autoAppliedRef.current = false;
    appliedTemplateRef.current = { id: '', packageCount: 0, bodyCount: 0 };
    if (packageInputRef.current) packageInputRef.current.value = '';
    if (bodyInputRef.current) bodyInputRef.current.value = '';
    setUploadMenuChannel(null);
    setLibraryOpen(false);
    setLibraryPickChannel(null);
    setLibrarySelection([]);
    imageSpaceRowsRef.current = [];
  }, [open, skc]);

  useEffect(() => {
    if (!open || !shopId || !skc) {
      setTemplates([]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      let supplierCode = String(productMeta?.supplierCode || '').trim();
      // 缺货号时补拉本地商品元数据，按款号自动套用实拍图库
      if (!supplierCode) {
        try {
          const metaMap = await loadProductMetaBySkc(shopId, [skc]);
          const loaded = metaMap?.[skc] || {};
          supplierCode = String(loaded.supplierCode || '').trim();
        } catch {
          /* 无货号时不自动套用 */
        }
      }
      if (cancelled) return;
      try {
        // 下拉展示全量模板；自动套用按款号匹配（不按类目）
        const res = await temuPlatformAPI.listPhotoTemplates(shopId, {
          page: 1,
          pageSize: 100,
        });
        const items = Array.isArray(res?.items) ? res.items : [];
        if (cancelled) return;
        const styleNo = styleNoFromSupplierCode(
          supplierCode,
          items.map((item) => item?.styleNo).filter(Boolean),
        );
        const styleKey = String(styleNo || '').trim().toLowerCase();
        // 下拉：同款号置顶，选项展示款号；自动套用仍只按款号（不按类目）
        const sorted = [...items].sort((a, b) => {
          const aMatch = styleKey && String(a?.styleNo || '').trim().toLowerCase() === styleKey ? 1 : 0;
          const bMatch = styleKey && String(b?.styleNo || '').trim().toLowerCase() === styleKey ? 1 : 0;
          if (aMatch !== bMatch) return bMatch - aMatch;
          const ap = Number(a?.priority) || 0;
          const bp = Number(b?.priority) || 0;
          if (ap !== bp) return bp - ap;
          return String(a?.name || '').localeCompare(String(b?.name || ''), 'zh');
        });
        setTemplates(sorted);
        if (autoAppliedRef.current || !styleKey) return;
        const styleMatched = sorted.filter(
          (item) => String(item?.styleNo || '').trim().toLowerCase() === styleKey
            && item.enabled !== false && item.enabled !== 0,
        );
        const picked = pickHighestPriorityTemplate(styleMatched);
        if (!picked?.id) return;
        autoAppliedRef.current = true;
        setSelectedTemplateId(String(picked.id));
        setTemplateBusy(true);
        try {
          const { pkg, body } = await loadAndConvertPhotoTemplate(shopId, picked.id);
          if (cancelled) return;
          if (!pkg.length && !body.length) {
            showToast(t('compliance.lbNoLib'), 'warning');
            return;
          }
          setPackageItems(pkg);
          setBodyItems(body);
          appliedTemplateRef.current = {
            id: String(picked.id),
            packageCount: pkg.length,
            bodyCount: body.length,
          };
          showToast(t('compliance.lbAppliedStyle', { name: picked.name || t('compliance.lbLibName') }), 'success');
        } catch (e) {
          if (!cancelled) showToast(e?.message || t('compliance.lbApplyLibFail'), 'error');
        } finally {
          if (!cancelled) setTemplateBusy(false);
        }
      } catch {
        if (!cancelled) setTemplates([]);
      }
    })();
    return () => { cancelled = true; };
  }, [open, shopId, skc, productMeta?.supplierCode, t]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !saving && !uploadingChannel && !previewSrc && !templateBusy && !cropping) {
        onClose?.();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, saving, uploadingChannel, previewSrc, templateBusy, cropping, onClose]);

  const rememberImageSpaceRows = useCallback((rows) => {
    if (!Array.isArray(rows) || rows.length === 0) return;
    imageSpaceRowsRef.current = appendUniqueImageSpaceRows(imageSpaceRowsRef.current, rows);
  }, []);

  if (!open) return null;

  const busy = saving || Boolean(uploadingChannel) || templateBusy || cropping;
  const totalCount = packageItems.length + bodyItems.length;
  const channelRemainSlots = (channel) => {
    const count = channel === 'package' ? packageItems.length : bodyItems.length;
    return Math.max(0, PHOTO_MAX_PER_CHANNEL - count);
  };

  const uploadAndConvert = async (file) => {
    if (!isAllowedPhotoFile(file)) throw new Error(i18n.t('compliance.lbPngOnly', { ns: 'workbench' }));
    const aspectOk = await ensureAspectFile(file);
    await validatePhotoFile(aspectOk);
    // 先落本地原图（供保存模板），再转平台 URL（供立即绑定）
    const stored = await temuPlatformAPI.storePhotoTemplateOriginal(shopId, aspectOk);
    const originalUrl = String(stored?.originalUrl || '').trim();
    if (!originalUrl) {
      throw new Error(i18n.t('compliance.lbOriginalFail', { ns: 'workbench' }));
    }
    const res = await temuPlatformAPI.uploadSkcLabelPicture(shopId, aspectOk);
    const info = res?.info && typeof res.info === 'object' ? res.info : res;
    const imageUrl = info?.imageUrl || res?.imageUrl;
    const imageMd5 = info?.imageMd5 || res?.imageMd5;
    if (!imageUrl || !imageMd5) {
      throw new Error(i18n.t('compliance.lbConvertEmpty', { ns: 'workbench' }));
    }
    return {
      id: newPhotoItemId(),
      name: stored?.name || aspectOk.name || file.name || t('compliance.lbPhoto'),
      originalUrl,
      previewUrl: String(
        stored?.previewUrlDisplay
        || stored?.originalUrlDisplay
        || stored?.viewUrl
        || stored?.previewUrl
        || originalUrl,
      ).trim() || originalUrl,
      imageUrl,
      imageMd5,
    };
  };

  const pickFiles = async (channel, fileList) => {
    if (!shopId) {
      showToast(t('returnList.pickShop'), 'warning');
      return;
    }
    const files = Array.from(fileList || []).filter(Boolean);
    if (!files.length) return;
    const remainSlots = channelRemainSlots(channel);
    const channelLabel = channel === 'package' ? t('compliance.lbPack') : t('compliance.lbBody');
    if (remainSlots <= 0) {
      showToast(t('compliance.lbChannelMax', { channel: channelLabel, max: PHOTO_MAX_PER_CHANNEL }), 'warning');
      return;
    }
    const picked = files.slice(0, remainSlots);
    if (picked.length < files.length) {
      showToast(t('compliance.lbOverChannel', { count: picked.length, channel: channelLabel, max: PHOTO_MAX_PER_CHANNEL }), 'warning');
    }
    setUploadingChannel(channel);
    try {
      const uploaded = [];
      for (const file of picked) {
        uploaded.push(await uploadAndConvert(file));
      }
      if (channel === 'package') {
        setPackageItems((prev) => [...prev, ...uploaded]);
      } else {
        setBodyItems((prev) => [...prev, ...uploaded]);
      }
      showToast(
        uploaded.length > 1
          ? t('compliance.lbConverted', { count: uploaded.length })
          : t('compliance.lbImageConverted'),
        'success',
      );
    } catch (e) {
      const msg = String(e?.message || '');
      if (msg.includes('已取消裁剪')) {
        showToast(t('compliance.lbCropSkipUpload'), 'info');
      } else {
        showToast(msg || t('compliance.lbConvertFail'), 'error');
      }
    } finally {
      setUploadingChannel(null);
      if (channel === 'package' && packageInputRef.current) packageInputRef.current.value = '';
      if (channel === 'body' && bodyInputRef.current) bodyInputRef.current.value = '';
    }
  };

  const pickFromImageSpace = async (channel, records) => {
    const picked = (Array.isArray(records) ? records : []).filter(Boolean);
    if (!picked.length) return;
    const remainSlots = channelRemainSlots(channel);
    const channelLabel = channel === 'package' ? t('compliance.lbPack') : t('compliance.lbBody');
    if (remainSlots <= 0) {
      showToast(t('compliance.lbChannelMax', { channel: channelLabel, max: PHOTO_MAX_PER_CHANNEL }), 'warning');
      return;
    }
    const batch = picked.slice(0, remainSlots);
    if (batch.length < picked.length) {
      showToast(t('compliance.lbOverBatch', { count: batch.length }), 'warning');
    }
    setUploadingChannel(channel);
    try {
      const uploaded = [];
      for (const image of batch) {
        const file = await imageSpaceRecordToUploadFile(image);
        uploaded.push(await uploadAndConvert(file));
      }
      if (channel === 'package') {
        setPackageItems((prev) => [...prev, ...uploaded]);
      } else {
        setBodyItems((prev) => [...prev, ...uploaded]);
      }
      showToast(
        uploaded.length > 1 ? t('compliance.lbPicked', { count: uploaded.length }) : t('compliance.lbImagePicked'),
        'success',
      );
    } catch (e) {
      const msg = String(e?.message || '');
      if (msg.includes('已取消裁剪')) {
        showToast(t('compliance.lbCropSkipPick'), 'info');
      } else {
        showToast(msg || t('compliance.lbPickFail'), 'error');
      }
    } finally {
      setUploadingChannel(null);
    }
  };

  const openUploadMenu = (channel) => {
    if (busy || !shopId) return;
    setUploadMenuChannel(channel);
  };

  const handleLibraryClose = () => {
    setLibrarySelection([]);
    setLibraryOpen(false);
    setLibraryPickChannel(null);
  };

  const handleLibraryToggle = (imageId) => {
    const id = String(imageId);
    const channel = libraryPickChannel;
    const remain = channel ? channelRemainSlots(channel) : PHOTO_MAX_PER_CHANNEL;
    setLibrarySelection((prev) => {
      if (prev.some((x) => String(x) === id)) {
        return prev.filter((x) => String(x) !== id);
      }
      if (prev.length >= remain) {
        showToast(t('compliance.lbRemain', { count: remain }), 'warning');
        return prev;
      }
      return [...prev, id];
    });
  };

  const handleLibraryConfirm = async (positionArray) => {
    const channel = libraryPickChannel;
    if (!channel) return;
    const ids = (positionArray || []).filter((x) => x != null && x !== '');
    if (ids.length === 0) {
      showToast(t('compliance.lbPickOne'), 'info');
      return;
    }
    const remain = channelRemainSlots(channel);
    const pickedIds = ids.slice(0, remain);
    const records = pickedIds
      .map((id) => imageSpaceRowsRef.current.find((row) => String(row?.id) === String(id)))
      .filter(Boolean);
    if (records.length === 0) {
      showToast(t('compliance.lbBadImages'), 'warning');
      return;
    }
    handleLibraryClose();
    await pickFromImageSpace(channel, records);
  };

  const removeItem = (channel, id) => {
    const drop = (list) => {
      const next = [];
      list.forEach((item) => {
        if (item.id === id) {
          if (item.previewUrl) URL.revokeObjectURL(item.previewUrl);
          return;
        }
        next.push(item);
      });
      return next;
    };
    if (channel === 'package') setPackageItems((prev) => drop(prev));
    else setBodyItems((prev) => drop(prev));
  };

  const openPreview = (item) => {
    const src = item?.previewUrl || item?.originalUrl || item?.imageUrl || '';
    if (!src) return;
    setPreviewSrc(src);
    setPreviewAlt(item?.name || t('compliance.lbPreviewTitle'));
  };

  const submit = async () => {
    if (!shopId || !skc) return;
    if (!packageItems.length && !bodyItems.length) {
      showToast(t('compliance.lbNeedPhoto'), 'error');
      return;
    }
    setSaving(true);
    try {
      const bindResult = await bindSkcPhotoLabels(
        shopId,
        skc,
        packageItems,
        bodyItems,
        resolvePurePhotoTemplateId(packageItems, bodyItems, appliedTemplateRef.current),
      );
      const statusHint = photoStatusDisplay(bindResult?.photoStatus)?.label;
      showToast(
        statusHint && statusHint !== '—'
          ? t('compliance.lbSubmitted', { status: statusHint })
          : t('compliance.lbSubmittedRecog'),
        'success',
      );
      onSaved?.();
    } catch (e) {
      showToast(e?.message || t('shop.bindFailed'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const applyPhotoTemplate = async () => {
    if (!shopId || !selectedTemplateId) {
      showToast(t('compliance.lbPickLib'), 'error');
      return;
    }
    setTemplateBusy(true);
    try {
      const { pkg, body } = await loadAndConvertPhotoTemplate(shopId, selectedTemplateId);
      if (!pkg.length && !body.length) {
        showToast(t('compliance.lbTplEmpty'), 'error');
        return;
      }
      if (pkg.length > PHOTO_MAX_PER_CHANNEL || body.length > PHOTO_MAX_PER_CHANNEL) {
        showToast(t('compliance.lbTplTrunc', { max: PHOTO_MAX_PER_CHANNEL }), 'warning');
      }
      setPackageItems(pkg);
      setBodyItems(body);
      appliedTemplateRef.current = {
        id: String(selectedTemplateId),
        packageCount: pkg.length,
        bodyCount: body.length,
      };
      showToast(t('compliance.lbAppliedTpl', { pkg: pkg.length, body: body.length }), 'success');
    } catch (e) {
      showToast(e?.message || t('compliance.lbApplyTplFail'), 'error');
    } finally {
      setTemplateBusy(false);
    }
  };

  const savePhotoTemplate = async () => {
    if (!shopId) {
      showToast(t('returnList.pickShop'), 'error');
      return;
    }
    const name = String(templateName || '').trim();
    if (!name) {
      showToast(t('compliance.lbNeedTplName'), 'error');
      return;
    }
    if (totalCount <= 0) {
      showToast(t('compliance.lbNeedPhotoSave'), 'error');
      return;
    }
    const missingOriginal = [...packageItems, ...bodyItems].some(
      (item) => !String(item?.originalUrl || '').trim(),
    );
    if (missingOriginal) {
      showToast(t('compliance.lbMissingOriginal'), 'error');
      return;
    }
    setTemplateBusy(true);
    try {
      await temuPlatformAPI.createPhotoTemplate(shopId, {
        name,
        payload: {
          packageImages: packageItems.map((item) => ({
            originalUrl: item.originalUrl,
            previewUrl: item.previewUrl || item.originalUrl,
            name: item.name,
            ...(item.imageUrl ? { imageUrl: item.imageUrl } : {}),
            ...(item.imageMd5 ? { imageMd5: item.imageMd5 } : {}),
          })),
          bodyImages: bodyItems.map((item) => ({
            originalUrl: item.originalUrl,
            previewUrl: item.previewUrl || item.originalUrl,
            name: item.name,
            ...(item.imageUrl ? { imageUrl: item.imageUrl } : {}),
            ...(item.imageMd5 ? { imageMd5: item.imageMd5 } : {}),
          })),
        },
      });
      showToast(t('compliance.lbSavedLib'), 'success');
      setTemplateName('');
      const listRes = await temuPlatformAPI.listPhotoTemplates(shopId, { page: 1, pageSize: 50 });
      setTemplates(Array.isArray(listRes?.items) ? listRes.items : []);
    } catch (e) {
      showToast(e?.message || t('compliance.lbSaveLibFail'), 'error');
    } finally {
      setTemplateBusy(false);
    }
  };

  const renderChannel = (channel, title, items, inputRef, anchorRef) => {
    const channelBusy = uploadingChannel === channel;
    const channelDragOver = dragOverChannel === channel;
    const remainSlots = channelRemainSlots(channel);
    return (
      <div className="tpo-img-upload tpo-photo-bind__channel">
        <div className="tpo-img-upload__head">
          <span className="tpo-field__label">{title}</span>
          <span className="tpo-img-upload__hint">{t('compliance.lbSheet', { count: `${items.length}/${PHOTO_MAX_PER_CHANNEL}` })}</span>
        </div>
        <div className="tpo-img-upload__grid">
          {items.map((item) => (
            <div key={item.id} className="tpo-img-upload__thumb">
              <button
                type="button"
                className="tpo-img-upload__thumb-hit"
                title={t('compliance.lbPreviewLarge')}
                onClick={() => openPreview(item)}
              >
                <img src={item.previewUrl || item.originalUrl || item.imageUrl} alt={item.name || title} />
              </button>
              <button
                type="button"
                className="tpo-img-upload__preview"
                onClick={() => openPreview(item)}
              >
                {t('compliance.lbPreview')}
              </button>
              <button
                type="button"
                className="tpo-img-upload__remove"
                aria-label={t('compliance.lbRemove', { title })}
                disabled={busy}
                onClick={() => removeItem(channel, item.id)}
              >
                ×
              </button>
            </div>
          ))}
          {remainSlots > 0 ? (
            <button
              ref={anchorRef}
              type="button"
              className={[
                'tpo-img-upload__drop',
                channelDragOver ? 'tpo-img-upload__drop--over' : '',
                channelBusy ? 'tpo-img-upload__drop--busy' : '',
              ].filter(Boolean).join(' ')}
              disabled={busy || !shopId}
              aria-busy={channelBusy}
              aria-haspopup="menu"
              onClick={() => openUploadMenu(channel)}
              onDragEnter={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!busy && shopId) setDragOverChannel(channel);
              }}
              onDragOver={(e) => {
                e.preventDefault();
                e.stopPropagation();
                if (!busy && shopId) setDragOverChannel(channel);
              }}
              onDragLeave={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setDragOverChannel((prev) => (prev === channel ? null : prev));
              }}
              onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setDragOverChannel(null);
                if (busy || !shopId) return;
                pickFiles(channel, e.dataTransfer?.files);
              }}
            >
              {channelBusy ? (
                <span className="tpo-img-upload__busy">
                  <span className="tpo-img-upload__spinner" />
                  <span className="tpo-img-upload__busy-text">{t('compliance.lbBusy')}</span>
                </span>
              ) : (
                <>
                  <span className="tpo-img-upload__plus">+</span>
                  <span className="tpo-img-upload__sub">{channelDragOver ? t('compliance.lbDrop') : t('compliance.lbClickUpload')}</span>
                </>
              )}
            </button>
          ) : null}
        </div>
        <input
          ref={inputRef}
          type="file"
          accept={PHOTO_ACCEPT}
          multiple
          hidden
          disabled={busy || !shopId}
          onChange={(e) => pickFiles(channel, e.target.files)}
        />
      </div>
    );
  };

  return createPortal(
    <div className="tpo-batch-modal tpo-cert-modal" role="presentation">
      <button
        type="button"
        className="tpo-batch-modal__backdrop"
        aria-label={t('moduleShell.close')}
        onClick={() => { if (!busy) onClose?.(); }}
      />
      <div
        className="tpo-batch-modal__panel tpo-cert-modal__panel tpo-photo-bind-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="tpo-label-bind-title"
      >
        <div className="tpo-batch-modal__head">
          <h2 id="tpo-label-bind-title">{t('compliance.lbUploadTitle', { skc })}</h2>
          <button type="button" className="tpo-btn tpo-btn--ghost tpo-btn--sm" onClick={onClose} disabled={busy}>
            {t('moduleShell.close')}
          </button>
        </div>
        <div className="tpo-batch-modal__body tpo-cert-modal__body">
          <div className="tpo-size-chart-template-bar tpo-photo-bind__template-bar">
            <select
              className="tpo-select"
              value={selectedTemplateId}
              disabled={busy || !shopId}
              onChange={(e) => setSelectedTemplateId(e.target.value)}
            >
              <option value="">{t('compliance.lbPickLibPh')}</option>
              {templates.map((item) => {
                const styleNo = String(item?.styleNo || '').trim();
                const counts = t('compliance.lbPackBody', { pkg: item.packageCount || 0, body: item.bodyCount || 0 });
                return (
                  <option key={String(item.id)} value={String(item.id)}>
                    {item.name}{styleNo ? ` · ${styleNo}` : ''}（{counts}）
                  </option>
                );
              })}
            </select>
            <button
              type="button"
              className="tpo-btn tpo-btn--secondary tpo-btn--sm"
              disabled={busy || !selectedTemplateId}
              onClick={applyPhotoTemplate}
            >
              {templateBusy ? t('moduleShell.processing') : t('compliance.lbApply')}
            </button>
            <input
              className="tpo-input"
              value={templateName}
              disabled={busy || !shopId}
              placeholder={t('compliance.lbSaveTplName')}
              onChange={(e) => setTemplateName(e.target.value)}
            />
            <button
              type="button"
              className="tpo-btn tpo-btn--primary tpo-btn--sm"
              disabled={busy || !shopId || totalCount === 0}
              onClick={savePhotoTemplate}
            >
              {t('compliance.lbSaveLib')}
            </button>
          </div>
          <div className="tpo-photo-bind__channels">
            {renderChannel('package', t('compliance.lbPackPhotos'), packageItems, packageInputRef, packageUploadAnchorRef)}
            {renderChannel('body', t('compliance.lbBodyPhotos'), bodyItems, bodyInputRef, bodyUploadAnchorRef)}
          </div>
          <p className="tpo-photo-bind__tips-text tpo-template-create-photo-rule">
            {t('compliance.lbPhotoHintTemu')}
          </p>
        </div>
        <div className="tpo-batch-modal__foot">
          <button type="button" className="tpo-btn tpo-btn--secondary tpo-btn--sm" onClick={onClose} disabled={busy}>
            {t('shop.cancel')}
          </button>
          <button
            type="button"
            className="tpo-btn tpo-btn--primary tpo-btn--sm"
            onClick={submit}
            disabled={busy || !shopId || totalCount === 0}
          >
            {saving ? t('compliance.lbBinding') : uploadingChannel ? t('compliance.lbConverting') : t('compliance.lbSubmit')}
          </button>
        </div>
      </div>
      <TemuImagePreviewLightbox
        src={previewSrc}
        alt={previewAlt}
        onClose={() => {
          setPreviewSrc('');
          setPreviewAlt('');
        }}
      />
      <TemuUploadSourceMenu
        open={uploadMenuChannel === 'package'}
        anchorRef={packageUploadAnchorRef}
        onClose={() => setUploadMenuChannel(null)}
        disabled={busy}
        onPickLocal={() => packageInputRef.current?.click()}
        onPickImageSpace={() => {
          setUploadMenuChannel(null);
          setLibraryPickChannel('package');
          setLibraryOpen(true);
        }}
      />
      <TemuUploadSourceMenu
        open={uploadMenuChannel === 'body'}
        anchorRef={bodyUploadAnchorRef}
        onClose={() => setUploadMenuChannel(null)}
        disabled={busy}
        onPickLocal={() => bodyInputRef.current?.click()}
        onPickImageSpace={() => {
          setUploadMenuChannel(null);
          setLibraryPickChannel('body');
          setLibraryOpen(true);
        }}
      />
      <LibraryModalContent
        isOpen={libraryOpen}
        onClose={handleLibraryClose}
        images={[]}
        selection={librarySelection}
        onToggle={handleLibraryToggle}
        maxSelection={libraryPickChannel ? channelRemainSlots(libraryPickChannel) : PHOTO_MAX_PER_CHANNEL}
        onConfirm={handleLibraryConfirm}
        onImagesAppended={rememberImageSpaceRows}
        uploadOriginSource="upload"
        showToast={showToast}
        deferUsageBump
        selectAllLabel={t('compliance.lbSelectLoaded')}
      />
      {cropModal}
    </div>,
    document.body,
  );
}

function LabelViewModal({ open, row, onClose }) {
  const { t } = useTranslation('workbench');
  const [previewSrc, setPreviewSrc] = useState('');
  const [previewAlt, setPreviewAlt] = useState('');

  useEffect(() => {
    if (!open) {
      setPreviewSrc('');
      setPreviewAlt('');
    }
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape' && !previewSrc) onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, previewSrc, onClose]);

  if (!open || !row) return null;

  const skc = pickField(row, 'skc', 'skcName') || '—';
  const elements = labelElements(row);
  const images = extractUploadedImages(row);

  return createPortal(
    <div className="tpo-batch-modal tpo-cert-modal" role="presentation">
      <button type="button" className="tpo-batch-modal__backdrop" aria-label={t('moduleShell.close')} onClick={onClose} />
      <div
        className="tpo-batch-modal__panel tpo-cert-modal__panel tpo-label-view-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="tpo-label-view-title"
      >
        <div className="tpo-batch-modal__head">
          <h2 id="tpo-label-view-title">{t('compliance.lbViewPhoto', { skc })}</h2>
          <button type="button" className="tpo-btn tpo-btn--ghost tpo-btn--sm" onClick={onClose}>
            {t('moduleShell.close')}
          </button>
        </div>
        <div className="tpo-batch-modal__body tpo-cert-modal__body">
          {images.length ? (
            <div className="tpo-label-view__images">
              {images.map((img) => (
                <figure key={img.url} className="tpo-label-view__figure">
                  <button
                    type="button"
                    className="tpo-label-view__img-btn"
                    title={t('compliance.lbPreviewLarge')}
                    onClick={() => {
                      setPreviewSrc(img.url);
                      setPreviewAlt(img.title || t('compliance.lbPreviewTitle'));
                    }}
                  >
                    <img src={img.url} alt={img.title} className="tpo-label-view__img" />
                  </button>
                  <figcaption>{img.title}</figcaption>
                </figure>
              ))}
            </div>
          ) : (
            <p className="tpo-batch-modal__hint">
              {row?.boundPhotos?.templateMissing
                ? t('compliance.lbTplGone')
                : t('compliance.lbNoBound')}
            </p>
          )}
          <div className="tpo-label-view__table-wrap">
            <table className="tpo-label-view__table">
              <thead>
                <tr>
                  <th>{t('compliance.lbColContent')}</th>
                  <th>{t('compliance.lbColPos')}</th>
                  <th>{t('compliance.lbColSites')}</th>
                  <th>{t('compliance.lbColReview')}</th>
                </tr>
              </thead>
              <tbody>
                {elements.length === 0 ? (
                  <tr>
                    <td colSpan={4}>{t('compliance.lbNoElements')}</td>
                  </tr>
                ) : (
                  elements.map((el, idx) => {
                    const status = Number(el.reviewStatus);
                    const sites = Array.isArray(el.siteList) ? el.siteList.filter(Boolean).join('、') : '—';
                    return (
                      <tr key={`${el.labelId || idx}-${el.labelName || ''}`}>
                        <td>{el.labelName || el.labelId || '—'}</td>
                        <td>{LABEL_GROUP[Number(el.labelGroup)] ? t(Number(el.labelGroup) === 1 ? 'compliance.lbBody' : 'compliance.lbPack') : '—'}</td>
                        <td>{sites || '—'}</td>
                        <td>{REVIEW_OUT_LABELS[status] ? t('compliance.lbOut' + status) : (status || '—')}</td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
        <div className="tpo-batch-modal__foot">
          <button type="button" className="tpo-btn tpo-btn--primary tpo-btn--sm" onClick={onClose}>
            {t('compliance.lbGotIt')}
          </button>
        </div>
      </div>
      <TemuImagePreviewLightbox
        src={previewSrc}
        alt={previewAlt}
        onClose={() => {
          setPreviewSrc('');
          setPreviewAlt('');
        }}
      />
    </div>,
    document.body,
  );
}

function FailReasonModal({ open, row, onClose }) {
  const { t } = useTranslation('workbench');
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open || !row) return null;

  const skc = pickField(row, 'skc', 'skcName') || '—';
  const items = collectFailReasons(row);

  return createPortal(
    <div className="tpo-batch-modal tpo-cert-modal" role="presentation">
      <button type="button" className="tpo-batch-modal__backdrop" aria-label={t('moduleShell.close')} onClick={onClose} />
      <div
        className="tpo-batch-modal__panel tpo-cert-modal__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="tpo-label-fail-title"
      >
        <div className="tpo-batch-modal__head">
          <h2 id="tpo-label-fail-title">{t('compliance.lbFailTitle', { skc })}</h2>
          <button type="button" className="tpo-btn tpo-btn--ghost tpo-btn--sm" onClick={onClose}>
            {t('moduleShell.close')}
          </button>
        </div>
        <div className="tpo-batch-modal__body tpo-cert-modal__body">
          {items.length === 0 ? (
            <p className="tpo-batch-modal__hint">{t('compliance.lbNoFail')}</p>
          ) : (
            <ul className="tpo-label-fail__list">
              {items.map((item, idx) => (
                <li key={`${idx}-${item.label}-${item.reason}`} className="tpo-label-fail__item">
                  {item.label ? (
                    <div className="tpo-label-fail__label">{item.label}</div>
                  ) : null}
                  <div className="tpo-label-fail__reason">{item.reason}</div>
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="tpo-batch-modal__foot">
          <button type="button" className="tpo-btn tpo-btn--primary tpo-btn--sm" onClick={onClose}>
            {t('compliance.lbGotIt')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function AutoBindErrorModal({ open, skc, reason, onClose }) {
  const { t } = useTranslation('workbench');
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div className="tpo-batch-modal tpo-cert-modal" role="presentation">
      <button type="button" className="tpo-batch-modal__backdrop" aria-label={t('moduleShell.close')} onClick={onClose} />
      <div
        className="tpo-batch-modal__panel tpo-cert-modal__panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby="tpo-auto-bind-error-title"
      >
        <div className="tpo-batch-modal__head">
          <h2 id="tpo-auto-bind-error-title">{t('compliance.lbLogTitle', { skc: skc || '—' })}</h2>
          <button type="button" className="tpo-btn tpo-btn--ghost tpo-btn--sm" onClick={onClose}>
            {t('moduleShell.close')}
          </button>
        </div>
        <div className="tpo-batch-modal__body tpo-cert-modal__body">
          <p className="tpo-label-fail__reason">{reason || t('compliance.lbNoError')}</p>
        </div>
        <div className="tpo-batch-modal__foot">
          <button type="button" className="tpo-btn tpo-btn--primary tpo-btn--sm" onClick={onClose}>
            {t('compliance.lbGotIt')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function LabelPrintModal({ open, shopId, skc, onClose }) {
  const { t } = useTranslation('workbench');
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [printing, setPrinting] = useState(false);
  const [selectedCode, setSelectedCode] = useState('');
  const [pdfUrls, setPdfUrls] = useState([]);

  useEffect(() => {
    if (!open || !shopId || !skc) return undefined;
    let cancelled = false;
    setLoading(true);
    setPdfUrls([]);
    setSelectedCode('');
    temuPlatformAPI.getLabelTemplates(shopId, { skcList: [skc] })
      .then((res) => {
        if (cancelled) return;
        const list = res?.labelTemplateList
          || res?.info?.labelTemplateList
          || res?.data?.labelTemplateList
          || (Array.isArray(res) ? res : [])
          || [];
        const rows = Array.isArray(list) ? list : [];
        // 平台可能按 SKC 嵌套
        const flat = rows.flatMap((item) => {
          if (Array.isArray(item?.labelTemplateList)) return item.labelTemplateList;
          if (item?.labelCode || item?.labelName) return [item];
          return [];
        });
        setTemplates(flat);
        if (flat[0]?.labelCode) setSelectedCode(String(flat[0].labelCode));
      })
      .catch((e) => {
        if (!cancelled) {
          setTemplates([]);
          showToast(e?.message || t('compliance.lbLoadTplFail'), 'error');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [open, shopId, skc, t]);

  if (!open) return null;

  const print = async () => {
    if (!shopId || !skc) return;
    setPrinting(true);
    try {
      const printRuleList = [{
        skc,
        labelType: 1,
        ...(selectedCode ? { labelCode: selectedCode, printNumer: 1 } : {}),
      }];
      const res = await temuPlatformAPI.printLabels(shopId, { printRuleList });
      const list = res?.printResultList
        || res?.info?.printResultList
        || res?.data
        || (Array.isArray(res) ? res : []);
      const urls = (Array.isArray(list) ? list : [])
        .map((item) => item?.pdfUrl || item?.labelPdfUrl || item?.url)
        .filter(Boolean);
      setPdfUrls(urls);
      if (!urls.length) {
        showToast(t('compliance.lbPrintNoPdf'), 'warning');
      } else {
        showToast(t('compliance.lbPrinted', { count: urls.length }), 'success');
      }
    } catch (e) {
      showToast(e?.message || t('compliance.lbPrintFail'), 'error');
    } finally {
      setPrinting(false);
    }
  };

  return createPortal(
    <div className="tpo-batch-modal tpo-cert-modal" role="presentation">
      <button type="button" className="tpo-batch-modal__backdrop" aria-label={t('moduleShell.close')} onClick={onClose} />
      <div className="tpo-batch-modal__panel tpo-cert-modal__panel" role="dialog" aria-modal="true">
        <div className="tpo-batch-modal__head">
          <h2>{t('compliance.lbPrintTitle', { skc })}</h2>
          <button type="button" className="tpo-btn tpo-btn--ghost tpo-btn--sm" onClick={onClose}>{t('moduleShell.close')}</button>
        </div>
        <div className="tpo-batch-modal__body tpo-cert-modal__body">
          {loading ? <p className="tpo-muted">{t('compliance.lbLoadingTpl')}</p> : null}
          {!loading && templates.length === 0 ? (
            <p className="tpo-muted">{t('compliance.lbNoTpl')}</p>
          ) : (
            <label className="tpo-field tpo-field--full">
              <span className="tpo-field__label">{t('compliance.lbTplField')}</span>
              <select
                className="tpo-select"
                value={selectedCode}
                onChange={(e) => setSelectedCode(e.target.value)}
              >
                {templates.map((tpl) => (
                  <option key={String(tpl.labelCode)} value={String(tpl.labelCode)}>
                    {tpl.labelName || tpl.labelCode}
                  </option>
                ))}
              </select>
            </label>
          )}
          {pdfUrls.length > 0 ? (
            <ul className="tpo-env-tree__materials">
              {pdfUrls.map((url) => (
                <li key={url}>
                  <a href={url} target="_blank" rel="noreferrer">{t('compliance.lbOpenPdf')}</a>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
        <div className="tpo-batch-modal__foot">
          <button type="button" className="tpo-btn tpo-btn--secondary tpo-btn--sm" onClick={onClose}>{t('shop.cancel')}</button>
          <button
            type="button"
            className="tpo-btn tpo-btn--primary tpo-btn--sm"
            onClick={print}
            disabled={printing || loading || !templates.length}
          >
            {printing ? t('compliance.lbPrinting') : t('compliance.lbPrint')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

/**
 * 合规资质 · 实拍图 / 标签（标签维保留兼容，侧栏入口仅挂实拍图）
 * @param {{
 *   mode?: 'photo'|'label',
 *   defaultReviewFilter?: string,
 *   syncKinds?: string[],
 *   active?: boolean,
 * }} props
 */
export default function SkcLabelPanel({
  mode = 'photo',
  defaultReviewFilter = 'all',
  syncKinds = ['compliance', 'photoAutoBind'],
  active = true,
}) {
  const isPhoto = mode !== 'label';
  const { t } = useTranslation('workbench');
  const { shopDimensionTabs, concreteScopeId } = useTemuPageShopScope();
  const shopId = concreteScopeId;
  const [searchParams] = useSearchParams();
  const noticeSkc = readNoticeSkc(searchParams);
  const [platformJsonOpen, setPlatformJsonOpen] = useState(false);

  const [reviewFilter, setReviewFilter] = useState(
    REVIEW_FILTER[defaultReviewFilter] ? defaultReviewFilter : 'all',
  );
  const [shelfFilterDraft, setShelfFilterDraft] = useState('all');
  const [shelfFilter, setShelfFilter] = useState('all');
  const [categoryIdDraft, setCategoryIdDraft] = useState('');
  const [productTypeIdDraft, setProductTypeIdDraft] = useState('');
  const [categoryIdFilter, setCategoryIdFilter] = useState('');
  const [productTypeIdFilter, setProductTypeIdFilter] = useState('');
  const [categories, setCategories] = useState([]);
  const [skcDraft, setSkcDraft] = useState('');
  const [skcQuery, setSkcQuery] = useState('');
  const [idTypeDraft, setIdTypeDraft] = useState(mode !== 'label' ? 'spu' : 'skc');
  const [idTypeQuery, setIdTypeQuery] = useState(mode !== 'label' ? 'spu' : 'skc');
  const [batchOpen, setBatchOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [productMeta, setProductMeta] = useState({});
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(TEMU_OPS_DEFAULT_PAGE_SIZE);
  const [bindSkc, setBindSkc] = useState('');
  const [viewRow, setViewRow] = useState(null);
  const [failRow, setFailRow] = useState(null);
  const [printSkc, setPrintSkc] = useState('');
  /** 当前筛选项下的可见条数（用于分页 total） */
  const [listTotal, setListTotal] = useState(0);
  const [dimensionStats, setDimensionStats] = useState(() => emptyPhotoDimensionStats());
  const [persistedSyncedAt, setPersistedSyncedAt] = useState('');
  const [lastSyncedAt, setLastSyncedAt] = useState('');
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  const [deleting, setDeleting] = useState(false);
  const [seedingDemo, setSeedingDemo] = useState(false);
  const [autoBindModalOpen, setAutoBindModalOpen] = useState(false);
  const [autoBindErrors, setAutoBindErrors] = useState({});
  const [autoBindErrorSkc, setAutoBindErrorSkc] = useState('');
  const skcCacheRef = useRef([]);
  const skcPayloadRef = useRef({});
  const allFilteredRowsRef = useRef([]);
  const loadGenRef = useRef(0);
  const { startJob, onTrackDone, isBusy } = useTemuSyncJobs();

  const categoryOptions = useMemo(
    () => flattenLeafCategories(categories).map((opt) => ({
      value: opt.categoryId,
      label: opt.label,
      productTypeId: opt.productTypeId,
    })),
    [categories],
  );

  useEffect(() => {
    if (!noticeSkc) return;
    setSkcDraft(noticeSkc);
    setSkcQuery(noticeSkc);
    setPage(1);
  }, [noticeSkc, shopId]);

  useEffect(() => {
    if (!shopId || !isPhoto || !active) {
      if (!active) return undefined;
      setCategories([]);
      return undefined;
    }
    let cancelled = false;
    temuPlatformAPI.getShopCategories(shopId)
      .then((res) => {
        if (cancelled) return;
        setCategories(res?.categories ?? res?.items ?? res?.list ?? res ?? []);
      })
      .catch(() => {
        if (!cancelled) setCategories([]);
      });
    return () => { cancelled = true; };
  }, [shopId, isPhoto, active]);

  const loadSkcPool = useCallback(async ({ force = false } = {}) => {
    if (!shopId) {
      skcCacheRef.current = [];
      skcPayloadRef.current = {};
      return [];
    }
    if (!force && skcCacheRef.current.length > 0) {
      return skcCacheRef.current;
    }
    const pageSizePool = 100;
    const payloadMap = {};
    const names = [];
    let pageNo = 1;
    let total = 0;
    // 后端单页上限 100；必须翻完全部缓存页，否则分页 total 与名单脱节会出现空页
    while (true) {
      const res = await temuPlatformAPI.listComplianceSkc(shopId, {
        source: 'cached',
        pageNum: pageNo,
        pageSize: pageSizePool,
        bindStatus: 'all',
        mode: isPhoto ? 'photo' : undefined,
      });
      const list = extractTemuList(res) || [];
      total = Number(res?.total) || total;
      list.forEach((row) => {
        const skc = String(pickField(row, 'skcName', 'skc', 'skcCode') || '').trim();
        if (!skc) return;
        names.push(skc);
        if (row?.payload && typeof row.payload === 'object') {
          payloadMap[skc] = row.payload;
        }
      });
      if (!list.length || names.length >= total || list.length < pageSizePool) break;
      pageNo += 1;
      if (pageNo > 100) break;
    }
    skcCacheRef.current = names;
    skcPayloadRef.current = payloadMap;
    return names;
  }, [shopId, isPhoto]);

  const load = useCallback(async ({ refreshPool = false } = {}) => {
    if (!active) return;
    if (!shopId) {
      setRows([]);
      setProductMeta({});
      setListTotal(0);
      allFilteredRowsRef.current = [];
      setDimensionStats(emptyPhotoDimensionStats());
      return;
    }
    const gen = ++loadGenRef.current;
    setLoading(true);
    const stillCurrent = () => gen === loadGenRef.current;
    const metaOpts = { categories };
    try {
      let skcList = parseBatchTokens(skcQuery).slice(0, BATCH_QUERY_LIMIT);
      if (!isPhoto) {
        if (!skcList.length) {
          skcList = await loadSkcPool({ force: refreshPool });
        } else {
          await loadSkcPool({ force: refreshPool });
        }
        if (!stillCurrent()) return;
        if (!skcList.length) {
          setRows([]);
          setProductMeta({});
          setListTotal(0);
          allFilteredRowsRef.current = [];
          setDimensionStats(emptyPhotoDimensionStats());
          setSelectedKeys(new Set());
          if (parseBatchTokens(skcQuery).length > 0) {
            showToast(t('compliance.lbNoQuerySkc'), 'warning');
          }
          return;
        }
      } else if (skcList.length) {
        // 实拍图手输 SKC 时仍刷新本地 payload，供回退路径使用
        await loadSkcPool({ force: refreshPool }).catch(() => []);
        if (!stillCurrent()) return;
      } else {
        // 无手输时预热池，供 photos 空结果时回退
        skcList = await loadSkcPool({ force: refreshPool }).catch(() => []);
        if (!stillCurrent()) return;
      }

      // 标签维：按合规 SKC 池展示，侧重打印；不走实拍审核态过滤
      if (!isPhoto) {
        setListTotal(skcList.length);
        setDimensionStats({ ...emptyPhotoDimensionStats(), all: skcList.length });
        const maxPage = Math.max(1, Math.ceil(skcList.length / pageSize) || 1);
        if (page > maxPage) {
          setPage(maxPage);
          return;
        }
        const start = (page - 1) * pageSize;
        const pageSkcs = skcList.slice(start, start + pageSize);
        const meta = await loadProductMetaBySkc(shopId, pageSkcs, metaOpts);
        if (!stillCurrent()) return;
        const mappedAll = skcList.map((skc) => {
          const payload = skcPayloadRef.current[skc] || {};
          const shelf = pickField(payload, 'skcShelfStatus');
          return {
            ...payload,
            skc,
            ...(shelf != null && shelf !== '' ? { skcShelfStatus: shelf } : {}),
          };
        });
        allFilteredRowsRef.current = mappedAll;
        setRows(pageSkcs.map((skc) => {
          const payload = skcPayloadRef.current[skc] || {};
          const m = meta[skc] || {};
          const shelf = pickField(payload, 'skcShelfStatus') ?? m.skcShelfStatus;
          return {
            ...payload,
            skc,
            ...(shelf != null && shelf !== '' ? { skcShelfStatus: shelf } : {}),
          };
        }));
        setProductMeta(meta);
        return;
      }

      // 实拍图：优先商品合规 /compliance/photos（状态=同步后的 photoStatus）
      const tokens = parseBatchTokens(skcQuery).slice(0, BATCH_QUERY_LIMIT);
      const photoStatus = mapReviewFilterToPhotoStatus(reviewFilter);
      const photoQuery = {
        mode: 'photo',
        pageNum: page,
        pageSize,
        status: photoStatus,
        photoStatus,
      };
      if (tokens.length === 1) {
        if (idTypeQuery === 'title') {
          photoQuery.keyword = tokens[0];
        } else {
          const n = Number(tokens[0]);
          if (Number.isFinite(n) && n > 0) {
            photoQuery.productId = n;
            photoQuery.skcName = tokens[0];
          } else {
            photoQuery.keyword = tokens[0];
          }
        }
      } else if (tokens.length > 1) {
        if (idTypeQuery === 'title') {
          photoQuery.keyword = tokens[0];
        } else {
          // 多 SKC/SPU：服务端仅支持单 productId，逐个查后合并
          photoQuery._multiTokens = tokens;
        }
      }

      let photoRes;
      let multiMerged = null;
      try {
        if (Array.isArray(photoQuery._multiTokens) && photoQuery._multiTokens.length > 1) {
          const multiTokens = photoQuery._multiTokens;
          delete photoQuery._multiTokens;
          const chunks = await Promise.all(multiTokens.map((token) => {
            const n = Number(token);
            const q = {
              ...photoQuery,
              pageNum: 1,
              pageSize: 20,
            };
            if (Number.isFinite(n) && n > 0) {
              q.productId = n;
              q.skcName = token;
            } else {
              q.keyword = token;
            }
            return temuPlatformAPI.listComplianceSkc(shopId, q).catch(() => null);
          }));
          const seen = new Set();
          const merged = [];
          chunks.forEach((res) => {
            (extractTemuList(res) || []).forEach((row) => {
              const key = String(pickField(row, 'skc', 'skcName', 'productId', 'id') || '');
              if (!key || seen.has(key)) return;
              seen.add(key);
              merged.push(row);
            });
          });
          multiMerged = {
            items: merged,
            total: merged.length,
            totalElements: merged.length,
          };
          photoRes = multiMerged;
        } else {
          delete photoQuery._multiTokens;
          photoRes = await temuPlatformAPI.listComplianceSkc(shopId, photoQuery);
        }
      } catch (e) {
        // 兼容旧路径：合规 photos 空时回退 skcLabel 池
        photoRes = null;
        if (!skcList.length) throw e;
      }

      let pageRows = [];
      let listTotalNext = 0;
      let serverPhotoPath = false;
      if (photoRes && (Number(photoRes.total) > 0 || extractTemuList(photoRes)?.length)) {
        serverPhotoPath = true;
        const allRows = (extractTemuList(photoRes) || []).map(normalizeCompliancePhotoRow);
        if (multiMerged) {
          listTotalNext = allRows.length;
          allFilteredRowsRef.current = allRows;
          const maxPage = Math.max(1, Math.ceil(allRows.length / pageSize) || 1);
          if (page > maxPage) {
            setPage(maxPage);
            return;
          }
          pageRows = allRows.slice((page - 1) * pageSize, (page - 1) * pageSize + pageSize);
        } else {
          pageRows = allRows;
          listTotalNext = Number(photoRes.total) || Number(photoRes.totalElements) || pageRows.length;
          allFilteredRowsRef.current = pageRows;
        }
        // 维度计数：分别查三态总数（仅统计用；多 SKC 合并时按本地统计）
        if (multiMerged) {
          const nextStats = emptyPhotoDimensionStats();
          nextStats.all = allFilteredRowsRef.current.length;
          allFilteredRowsRef.current.forEach((row) => {
            const dim = photoDimensionOf(row);
            if (nextStats[dim] != null) nextStats[dim] += 1;
          });
          if (!stillCurrent()) return;
          setDimensionStats(nextStats);
        } else if (photoRes?.statusCounts && typeof photoRes.statusCounts === 'object') {
          const nextStats = emptyPhotoDimensionStats();
          Object.keys(nextStats).forEach((key) => {
            if (key === 'all') return;
            nextStats[key] = Number(photoRes.statusCounts[key]) || 0;
          });
          nextStats.all = Number(photoRes.totalAll) || Object.keys(nextStats)
            .filter((k) => k !== 'all')
            .reduce((sum, k) => sum + (Number(nextStats[k]) || 0), listTotalNext);
          if (!stillCurrent()) return;
          setDimensionStats(nextStats);
        } else {
          const countBase = { ...photoQuery };
          delete countBase._multiTokens;
          const countEntries = await Promise.all(
            PHOTO_STATUS_TABS.filter((t) => t.id !== 'all').map(async (tab) => {
              const res = await temuPlatformAPI.listComplianceSkc(shopId, {
                ...countBase,
                pageNum: 1,
                pageSize: 1,
                status: tab.id,
                photoStatus: tab.id,
              });
              return [tab.id, Number(res?.total) || Number(res?.totalElements) || 0];
            }),
          );
          if (!stillCurrent()) return;
          const nextStats = emptyPhotoDimensionStats();
          countEntries.forEach(([id, count]) => {
            nextStats[id] = count;
          });
          nextStats.all = countEntries.reduce((sum, [, c]) => sum + c, 0) || listTotalNext;
          setDimensionStats(nextStats);
        }
      } else {
        // 回退：本地 payload / skc-label-list
        const cachedRows = [];
        const needPlatform = [];
        skcList.forEach((skc) => {
          const payload = skcPayloadRef.current[skc];
          if (payload && (Array.isArray(payload.skcLabelInfoList) || payload.skc || payload.skcName || payload.photoStatus)) {
            cachedRows.push(normalizeCompliancePhotoRow({
              ...payload,
              skc: pickField(payload, 'skc', 'skcName') || skc,
              photoStatus: pickField(payload, 'photoStatus') || undefined,
            }));
          } else {
            needPlatform.push(skc);
          }
        });
        let labelRows = [...cachedRows];
        if (needPlatform.length) {
          for (let i = 0; i < needPlatform.length; i += 100) {
            const batch = needPlatform.slice(i, i + 100);
            try {
              const res = await temuPlatformAPI.listSkcLabels(shopId, {
                pageNum: 1,
                pageSize: Math.min(100, batch.length),
                skcList: batch,
              });
              labelRows = [...labelRows, ...normalizeLabelRows(res).map(normalizeCompliancePhotoRow)];
            } catch (e) {
              if (!labelRows.length && i === 0) throw e;
            }
          }
        }
        let shelfRows = labelRows.filter((row) => matchesPhotoListFilters(row, 'all', shelfFilter));
        const hasCategoryFilter = Boolean(productTypeIdFilter || categoryIdFilter);
        if (hasCategoryFilter && shelfRows.length) {
          const metaBySkc = await loadProductMetaBySkc(
            shopId,
            shelfRows.map((row) => pickField(row, 'skc', 'skcName')).filter(Boolean),
            metaOpts,
          );
          if (!stillCurrent()) return;
          shelfRows = shelfRows.filter((row) => {
            const skc = String(pickField(row, 'skc', 'skcName') || '').trim();
            return metaMatchesCategoryFilter(metaBySkc[skc], {
              productTypeId: productTypeIdFilter,
              categoryId: categoryIdFilter,
            });
          });
        }
        const nextStats = emptyPhotoDimensionStats();
        nextStats.all = shelfRows.length;
        shelfRows.forEach((row) => {
          const dim = photoDimensionOf(row);
          if (nextStats[dim] != null) nextStats[dim] += 1;
        });
        if (!stillCurrent()) return;
        setDimensionStats(nextStats);
        const filtered = shelfRows.filter((row) => matchesPhotoListFilters(row, reviewFilter, 'all'));
        listTotalNext = filtered.length;
        allFilteredRowsRef.current = filtered;
        const maxPage = Math.max(1, Math.ceil(filtered.length / pageSize) || 1);
        if (page > maxPage) {
          setPage(maxPage);
          return;
        }
        pageRows = filtered.slice((page - 1) * pageSize, (page - 1) * pageSize + pageSize);
      }

      if (!stillCurrent()) return;
      setListTotal(listTotalNext);
      // multiMerged / fallback 已写入 allFilteredRowsRef；服务端单页路径仅缓存当前页
      if (serverPhotoPath && !multiMerged) {
        allFilteredRowsRef.current = pageRows;
      }

      // 类目筛选（服务端 photos 路径时在当前页做）
      let workingRows = pageRows;
      const hasCategoryFilter = Boolean(productTypeIdFilter || categoryIdFilter);
      let meta = await loadProductMetaBySkc(
        shopId,
        workingRows.map((row) => pickField(row, 'skc', 'skcName')).filter(Boolean),
        metaOpts,
      );
      if (!stillCurrent()) return;
      if (hasCategoryFilter) {
        workingRows = workingRows.filter((row) => {
          const skc = String(pickField(row, 'skc', 'skcName') || '').trim();
          return metaMatchesCategoryFilter(meta[skc], {
            productTypeId: productTypeIdFilter,
            categoryId: categoryIdFilter,
          });
        });
      }
      if (shelfFilter && shelfFilter !== 'all') {
        workingRows = workingRows.filter((row) => matchesPhotoListFilters(row, 'all', shelfFilter));
      }
      setRows(workingRows);
      setProductMeta(meta);
    } catch (e) {
      if (!stillCurrent()) return;
      setRows([]);
      setProductMeta({});
      setListTotal(0);
      allFilteredRowsRef.current = [];
      setDimensionStats(emptyPhotoDimensionStats());
      showToast(e?.message || t(isPhoto ? 'compliance.lbLoadPhotoFail' : 'compliance.lbLoadLabelFail'), 'error');
    } finally {
      if (stillCurrent()) setLoading(false);
    }
  }, [
    shopId,
    skcQuery,
    idTypeQuery,
    page,
    pageSize,
    reviewFilter,
    shelfFilter,
    productTypeIdFilter,
    categoryIdFilter,
    loadSkcPool,
    isPhoto,
    active,
    categories,
    t,
  ]);

  useEffect(() => {
    if (!active) return;
    load();
  }, [load, active]);

  useEffect(() => {
    setSkcDraft('');
    setSkcQuery('');
    setIdTypeDraft(isPhoto ? 'spu' : 'skc');
    setIdTypeQuery(isPhoto ? 'spu' : 'skc');
    setPage(1);
    setRows([]);
    setProductMeta({});
    setListTotal(0);
    setSelectedKeys(new Set());
    setLastSyncedAt('');
    setPersistedSyncedAt('');
    skcCacheRef.current = [];
    skcPayloadRef.current = {};
    allFilteredRowsRef.current = [];
    const next = REVIEW_FILTER[defaultReviewFilter] ? defaultReviewFilter : 'all';
    setReviewFilter(next);
    setShelfFilterDraft('all');
    setShelfFilter('all');
    setCategoryIdDraft('');
    setProductTypeIdDraft('');
    setCategoryIdFilter('');
    setProductTypeIdFilter('');
    setDimensionStats(emptyPhotoDimensionStats());
  }, [shopId, defaultReviewFilter, isPhoto]);

  useEffect(() => {
    if (!shopId) {
      setPersistedSyncedAt('');
      return undefined;
    }
    let cancelled = false;
    temuPlatformAPI.getShopSyncStatus(shopId)
      .then((res) => {
        if (cancelled) return;
        const modules = Array.isArray(res?.modules) ? res.modules : [];
        const compliance = modules.find((m) => String(m.module || '').toUpperCase() === 'COMPLIANCE');
        const at = compliance?.lastSyncEndAt || compliance?.lastRunAt || '';
        setPersistedSyncedAt(at ? String(at) : '');
      })
      .catch(() => {
        if (!cancelled) setPersistedSyncedAt('');
      });
    return () => { cancelled = true; };
  }, [shopId]);

  useEffect(() => {
    if (!shopId || !isPhoto) {
      setAutoBindErrors({});
      return undefined;
    }
    let cancelled = false;
    temuPlatformAPI.getPreferences()
      .then((res) => {
        if (cancelled) return;
        const prefs = res?.preferences || res || {};
        setAutoBindErrors(readComplianceAutoBindErrors(prefs, shopId, 'photo'));
      })
      .catch(() => {
        if (!cancelled) setAutoBindErrors({});
      });
    return () => { cancelled = true; };
  }, [shopId, isPhoto]);

  const totalSkc = listTotal;

  const handleSync = useCallback(async () => {
    if (!shopId) {
      showToast(t('publishRecord.pickAuthorized', { platform: 'TEMU' }), 'warning');
      return;
    }
    const track = await startJob({
      kind: 'compliance',
      shopId,
      label: t('publishRecord.syncUpdate'),
      silent: true,
    });
    if (!track?.id) return;
    await new Promise((resolve) => {
      const unsub = onTrackDone(track.id, async (result) => {
        unsub();
        if (result?.ok) {
          const view = result.view || {};
          const skcCount = Number(view.skcProcessed ?? view.skcTotal ?? view.imported) || 0;
          setLastSyncedAt(new Date().toISOString());
          await load({ refreshPool: true });
          const rawMessage = view.message || '';
          const emptySkc = skcCount <= 0 || /无可用\s*SKC/i.test(String(rawMessage));
          let message = rawMessage || (skcCount > 0 ? t('compliance.lbSyncDone') : t('compliance.lbSyncEmpty'));
          if (!emptySkc && isPhoto) {
            try {
              const auto = await maybeRunPhotoAutoBindAfterSync(shopId);
              if (auto?.ok > 0) {
                message = t('compliance.lbAutoExtra', { message, count: auto.ok });
                await load({ refreshPool: true });
              }
              if (auto?.fail > 0 || Object.keys(auto?.errorsBySkc || {}).length) {
                const prefRes = await temuPlatformAPI.getPreferences().catch(() => null);
                const prefs = prefRes?.preferences || prefRes || {};
                setAutoBindErrors(readComplianceAutoBindErrors(prefs, shopId, 'photo'));
              }
            } catch {
              /* 自动补传失败不影响同步成功提示 */
            }
          }
          showToast(message, emptySkc ? 'warning' : 'success');
        }
        resolve();
      });
    });
  }, [shopId, load, startJob, onTrackDone, isPhoto, t]);

  const handleSeedDemo = useCallback(async () => {
    if (!shopId) {
      showToast(t('publishRecord.pickAuthorized', { platform: 'TEMU' }), 'warning');
      return;
    }
    const ok = await showTemuConfirm({
      title: t('compliance.lbDemoTitle'),
      message: t('compliance.lbDemoMsg'),
      tone: 'default',
      confirmText: t('compliance.lbWriteDemo'),
      cancelText: t('shop.cancel'),
    });
    if (!ok) return;
    setSeedingDemo(true);
    try {
      const res = await temuPlatformAPI.seedPhotoComplianceDemo(shopId);
      const count = Array.isArray(res?.skcNames) ? res.skcNames.length : (Number(res?.cacheUpserted) || 0);
      const coverage = Array.isArray(res?.statusCoverage) ? res.statusCoverage.length : 0;
      showToast(
        count > 0
          ? (coverage
            ? t('compliance.lbSeededCoverage', { count, kinds: coverage })
            : t('compliance.lbSeeded', { count }))
          : t('compliance.lbDemoWritten'),
        'success',
      );
      setPage(1);
      setSkcQuery('');
      setSkcDraft('');
      setIdTypeDraft(isPhoto ? 'spu' : 'skc');
      setIdTypeQuery(isPhoto ? 'spu' : 'skc');
      setReviewFilter('all');
      setShelfFilterDraft('all');
      setShelfFilter('all');
      setCategoryIdDraft('');
      setProductTypeIdDraft('');
      setCategoryIdFilter('');
      setProductTypeIdFilter('');
      setSelectedKeys(new Set());
      await load({ refreshPool: true });
    } catch (e) {
      showToast(e?.message || t('compliance.lbLoadDemoFail'), 'error');
    } finally {
      setSeedingDemo(false);
    }
  }, [shopId, load, isPhoto, t]);

  const { refresh: onSync, refreshing: syncingLocal, cooldownSeconds: syncCooldown } = useGuardedRefresh(
    handleSync,
    { minIntervalMs: 8000 },
  );

  const syncing = syncingLocal || isBusy('compliance', shopId);

  const latestSyncedAt = lastSyncedAt || persistedSyncedAt;
  const latestSyncedLabel = latestSyncedAt
    ? t('tiktokCatalog.updatedAt', { time: formatSiteListSyncedAt(latestSyncedAt) })
    : t('tiktokCatalog.notSynced');

  const onQuery = () => {
    const tokens = parseBatchTokens(skcDraft).slice(0, BATCH_QUERY_LIMIT);
    setPage(1);
    setShelfFilter(SHELF_OPTIONS.some((o) => o.value === shelfFilterDraft) ? shelfFilterDraft : 'all');
    setCategoryIdFilter(categoryIdDraft);
    setProductTypeIdFilter(productTypeIdDraft);
    setIdTypeQuery(idTypeDraft);
    setSkcQuery(tokens.join(','));
  };

  const idTypeOption = PHOTO_ID_TYPE_OPTIONS.find((o) => o.value === idTypeDraft);
  const idTypeLabel = idTypeOption?.labelKey ? t(idTypeOption.labelKey) : (idTypeOption?.label || 'SKC');

  const onReviewDimensionChange = (next) => {
    if (!REVIEW_FILTER[next] || next === reviewFilter) return;
    setReviewFilter(next);
    setPage(1);
    setSelectedKeys(new Set());
  };

  const statusTabs = isPhoto ? (
    <div className="tpo-status-tabs" role="tablist" aria-label={t('compliance.lbRecogAria')}>
      {PHOTO_STATUS_TABS.map((tab) => {
        const active = reviewFilter === tab.id;
        const count = Number(dimensionStats[tab.id]) || 0;
        return (
          <button
            key={tab.id}
            type="button"
            role="tab"
            aria-selected={active}
            className={`tpo-status-tab${active ? ' tpo-status-tab--active' : ''}`}
            onClick={() => onReviewDimensionChange(tab.id)}
          >
            {tab.labelKey ? t(tab.labelKey) : (tab.id === 'all' ? t('tiktokCatalog.all') : t('compliance.ph_' + tab.id, { defaultValue: tab.label }))}
            {count > 0 ? <span className="tpo-status-tab__count">{count}</span> : null}
          </button>
        );
      })}
    </div>
  ) : null;

  const selectableKeys = useMemo(
    () => rows.map((row, index) => pickField(row, 'skc', 'skcName') || `row-${index}`),
    [rows],
  );
  const allSelected = selectableKeys.length > 0
    && selectableKeys.every((key) => selectedKeys.has(key));

  const toggleAll = () => {
    setSelectedKeys((prev) => togglePageSelection(prev, selectableKeys, allSelected));
  };

  const toggleOne = (key) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const colSpan = isPhoto ? 6 : 5;

  const emptyMessage = isPhoto
    ? t('compliance.lbEmptyPhotoPull', { platform: 'TEMU' })
    : t('compliance.lbEmptyLabelPull', { platform: 'TEMU' });

  const writeSkcExportCsv = useCallback((exportRows, kind) => {
    const emptyMsg = isPhoto ? t('compliance.lbNonePhoto') : t('compliance.lbNoneLabel');
    if (!exportRows.length) {
      showToast(emptyMsg, 'warning');
      return false;
    }
    if (isPhoto) {
      downloadCsv(
        `temu-spu-photos-${kind}-${Date.now()}.csv`,
        ['spu', 'title', 'shelfStatus', 'reviewStatus', 'labelContent', 'sites'],
        exportRows.map((row, index) => {
          const skc = pickField(row, 'skc', 'skcName') || `row-${index}`;
          const meta = productMeta[skc] || {};
          const photoStatus = photoStatusOf(row);
          const spu = meta.spuName
            || pickField(row, 'spu', 'spuName', 'productId')
            || skc;
          return {
            spu,
            title: meta.title || pickField(row, 'title', 'productTitle') || '',
            shelfStatus: shelfLabel(row),
            reviewStatus: photoStatus.label,
            labelContent: labelContentItems(row).join('、'),
            sites: siteListItems(row).join('、'),
          };
        }),
      );
      showToast(
        kind === 'selected'
          ? t('compliance.lbExportedPhotoSelected', { count: exportRows.length })
          : t('compliance.lbExportedPhoto', { count: exportRows.length }),
        'success',
      );
      return true;
    }
    downloadCsv(
      `temu-skc-labels-${kind}-${Date.now()}.csv`,
      ['skc', 'title', 'shelfStatus'],
      exportRows.map((row, index) => {
        const skc = pickField(row, 'skc', 'skcName') || `row-${index}`;
        const meta = productMeta[skc] || {};
        return {
          skc,
          title: meta.title || '',
          shelfStatus: shelfLabel(row),
        };
      }),
    );
    showToast(
      kind === 'selected'
        ? t('compliance.lbExportedLabelSelected', { count: exportRows.length })
        : t('compliance.lbExportedLabel', { count: exportRows.length }),
      'success',
    );
    return true;
  }, [isPhoto, productMeta, t]);

  const exportFiltered = useCallback(async () => {
    if (!isPhoto) {
      writeSkcExportCsv(allFilteredRowsRef.current, 'filtered');
      return;
    }
    // 服务端分页时当前 ref 可能只有本页：翻页拉全量后再导出
    if (!shopId || listTotal <= (allFilteredRowsRef.current?.length || 0)) {
      writeSkcExportCsv(allFilteredRowsRef.current, 'filtered');
      return;
    }
    try {
      const tokens = parseBatchTokens(skcQuery).slice(0, BATCH_QUERY_LIMIT);
      const photoStatus = mapReviewFilterToPhotoStatus(reviewFilter);
      const collected = [];
      let pageNo = 1;
      const size = 100;
      while (pageNo <= 50) {
        const q = {
          mode: 'photo',
          pageNum: pageNo,
          pageSize: size,
          status: photoStatus,
          photoStatus,
        };
        if (tokens.length === 1) {
          if (idTypeQuery === 'title') {
            q.keyword = tokens[0];
          } else {
            const n = Number(tokens[0]);
            if (Number.isFinite(n) && n > 0) {
              q.productId = n;
              q.skcName = tokens[0];
            } else {
              q.keyword = tokens[0];
            }
          }
        }
        const res = await temuPlatformAPI.listComplianceSkc(shopId, q);
        const list = (extractTemuList(res) || []).map(normalizeCompliancePhotoRow);
        collected.push(...list);
        const totalCount = Number(res?.total) || Number(res?.totalElements) || collected.length;
        if (!list.length || collected.length >= totalCount || list.length < size) break;
        pageNo += 1;
      }
      writeSkcExportCsv(collected.length ? collected : allFilteredRowsRef.current, 'filtered');
    } catch (e) {
      showToast(e?.message || t('compliance.lbExportFailed'), 'error');
    }
  }, [
    writeSkcExportCsv,
    isPhoto,
    shopId,
    listTotal,
    skcQuery,
    idTypeQuery,
    reviewFilter,
    t,
  ]);

  const exportPage = useCallback(() => {
    writeSkcExportCsv(rows, 'page');
  }, [rows, writeSkcExportCsv]);

  const exportSelected = useCallback(() => {
    const filtered = allFilteredRowsRef.current;
    const selected = filtered.filter((row, index) => {
      const key = pickField(row, 'skc', 'skcName') || `row-${index}`;
      return selectedKeys.has(key);
    });
    if (!selected.length) {
      showToast(t(isPhoto ? 'compliance.lbPickExportPhoto' : 'compliance.lbPickExportLabel'), 'warning');
      return;
    }
    writeSkcExportCsv(selected, 'selected');
  }, [selectedKeys, isPhoto, writeSkcExportCsv, t]);

  const onBatchDelete = async () => {
    if (!shopId || selectedKeys.size === 0) {
      showToast(t('compliance.lbPickDelete'), 'warning');
      return;
    }
    const skcNames = Array.from(selectedKeys)
      .map((s) => String(s || '').trim())
      .filter((s) => s && !s.startsWith('row-'));
    if (!skcNames.length) {
      showToast(t('compliance.lbMissingSkc'), 'error');
      return;
    }
    const productIds = skcNames
      .map((s) => Number(s))
      .filter((n) => Number.isFinite(n) && n > 0);
    const ok = await showTemuConfirm({
      title: t('returnList.batchDelete'),
      message: t('compliance.lbDeleteMsg', { count: skcNames.length, platform: 'TEMU' }),
      tone: 'danger',
      confirmText: t('returnList.delete'),
      cancelText: t('shop.cancel'),
    });
    if (!ok) return;
    setDeleting(true);
    try {
      const res = await temuPlatformAPI.batchDeleteComplianceSkc(shopId, { skcNames, productIds });
      const deleted = Number(res?.deleted);
      const count = Number.isFinite(deleted) && deleted >= 0 ? deleted : skcNames.length;
      if (count > 0) {
        showToast(t('compliance.lbDeleted', { count }), 'success');
      } else {
        showToast(t('certList.noneDeletedCache'), 'warning');
      }
      setSelectedKeys(new Set());
      await load({ refreshPool: true });
    } catch (e) {
      showToast(e?.message || t('shop.batchDeleteFailed'), 'error');
    } finally {
      setDeleting(false);
    }
  };

  const extraActions = [
    ...(isPhoto
      ? [{
        key: 'auto-rules',
        label: t('compliance.autoRule'),
        disabled: !shopId || seedingDemo || syncing || loading || deleting,
        onClick: () => setAutoBindModalOpen(true),
      }]
      : []),
    ...(isPhoto
      ? [{
        key: 'seed-demo',
        label: seedingDemo ? t('compliance.lbWriting') : t('compliance.lbLoadDemo'),
        disabled: !shopId || seedingDemo || syncing || loading || deleting,
        onClick: handleSeedDemo,
      }]
      : []),
  ];

  return (
    <>
      <TemuSyncProgressSlot kinds={syncKinds} />
      <ProductFilterCard
        toolbar={(
          <TemuOpsListToolbar
            selectedCount={selectedKeys.size}
            busy={syncing || loading || seedingDemo || deleting}
            syncKinds={syncKinds}
            quotaKind="compliance"
            onSync={() => {
              if (!shopId) {
                showToast(t('publishRecord.pickAuthorized', { platform: 'TEMU' }), 'warning');
                return;
              }
              onSync();
            }}
            syncing={syncing}
            syncCooldownSeconds={syncCooldown}
            syncDisabled={!shopId || syncing}
            lastSyncedLabel={latestSyncedLabel}
            syncHint={isPhoto
              ? t('compliance.lbSyncTemuPhoto')
              : t('compliance.lbSyncTemuLabel')}
            exportLabel={opsExportLabel(false)}
            exportDisabled={!shopId}
            exportMenu={buildOpsExportMenu({
              filteredCount: listTotal,
              pageCount: rows.length,
              selectedCount: selectedKeys.size,
              onExportFiltered: exportFiltered,
              onExportPage: exportPage,
              onExportSelected: exportSelected,
            })}
            onBatchDelete={onBatchDelete}
            deleteDisabled={!shopId || deleting}
            trailingExtra={(
              <button
                type="button"
                className="tpo-btn tpo-btn--ghost tpo-btn--sm"
                disabled={!shopId}
                title={shopId
                  ? t('compliance.lbViewJson')
                  : t('syncedLib.pickConcrete')}
                onClick={() => setPlatformJsonOpen(true)}
              >
                {t('compliance.lbPlatformJson')}
              </button>
            )}
            extraActions={extraActions}
          />
        )}
      >
        <div className="tpo-filter-bar">
          <div className="tpo-filter-bar__fields">
            <div className="tpo-field tpo-field--inline tpo-field--id-combo tpo-field--batch">
              {isPhoto ? (
                <select
                  className="tpo-select tpo-select--compact"
                  value={idTypeDraft}
                  disabled={!shopId}
                  onChange={(e) => setIdTypeDraft(e.target.value)}
                  aria-label={t('compliance.idType')}
                >
                  {PHOTO_ID_TYPE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.labelKey ? t(opt.labelKey) : opt.label}</option>
                  ))}
                </select>
              ) : (
                <span className="tpo-field__label">SKC</span>
              )}
              <input
                className="tpo-input"
                value={skcDraft}
                onChange={(e) => setSkcDraft(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && onQuery()}
                placeholder={isPhoto
                  ? t('publishRecord.idPlaceholder', { type: idTypeLabel })
                  : t('compliance.lbBatchSkc')}
                aria-label={isPhoto ? idTypeLabel : 'SKC'}
                disabled={!shopId}
              />
              <button
                type="button"
                className="tpo-batch-add-btn"
                aria-label={t('tiktokCatalog.batchInput', { label: isPhoto ? idTypeLabel : 'SKC' })}
                title={t('tiktokCatalog.batchQuery')}
                disabled={!shopId}
                onClick={() => setBatchOpen(true)}
              >
                <Plus size={16} strokeWidth={2.25} aria-hidden="true" />
              </button>
            </div>
            {isPhoto ? (
              <div className="tpo-field tpo-field--inline tpo-field--category">
                <span className="tpo-field__label">{t('compliance.lbCategoryName')}</span>
                <SearchableSelect
                  value={categoryIdDraft}
                  disabled={!shopId || loading}
                  placeholder={!shopId ? t('returnList.pickShop') : t('productTemplate.allCategories')}
                  options={categoryOptions}
                  onChange={(id) => {
                    const hit = categoryOptions.find((opt) => String(opt.value) === String(id));
                    const nextTypeId = hit?.productTypeId && String(hit.productTypeId) !== '0'
                      ? String(hit.productTypeId)
                      : '';
                    setCategoryIdDraft(id ? String(id) : '');
                    setProductTypeIdDraft(nextTypeId);
                  }}
                />
              </div>
            ) : null}
            {isPhoto ? (
              <div className="tpo-field tpo-field--inline tpo-field--status">
                <span className="tpo-field__label">{t('compliance.lbColStatus')}</span>
                <select
                  className="tpo-select tpo-select--status"
                  value={shelfFilterDraft}
                  disabled={!shopId}
                  onChange={(e) => setShelfFilterDraft(e.target.value)}
                  aria-label={t('compliance.lbShelfQuery')}
                >
                  {SHELF_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value}>{opt.labelKey ? t(opt.labelKey) : opt.label}</option>
                  ))}
                </select>
              </div>
            ) : null}
          </div>
          <div className="tpo-filter-bar__actions">
            <button
              type="button"
              className="tpo-btn tpo-btn--secondary tpo-btn--sm"
              disabled={!shopId}
              onClick={() => {
                setSkcDraft('');
                setSkcQuery('');
                setIdTypeDraft(isPhoto ? 'spu' : 'skc');
                setIdTypeQuery(isPhoto ? 'spu' : 'skc');
                setReviewFilter('all');
                setShelfFilterDraft('all');
                setShelfFilter('all');
                setCategoryIdDraft('');
                setProductTypeIdDraft('');
                setCategoryIdFilter('');
                setProductTypeIdFilter('');
                setPage(1);
                setSelectedKeys(new Set());
              }}
            >
              {t('returnList.reset')}
            </button>
            <button type="button" className="tpo-btn tpo-btn--primary tpo-btn--sm" onClick={onQuery} disabled={!shopId}>
              {t('shop.query')}
            </button>
          </div>
        </div>
      </ProductFilterCard>

      <ProductListCard
        tabs={stackShopDimensionTabs(shopDimensionTabs, statusTabs)}
        loading={Boolean(shopId) && loading}
        loadingLabel={t(isPhoto ? 'compliance.lbQueryPhoto' : 'compliance.lbQueryLabel')}
      >
        <div className="tpo-drafts__table-wrap tpo-certs-table-wrap">
          <table className="tpo-publish__table tpo-drafts__table tpo-certs-table">
            <colgroup>
              <col className="tpo-certs-col tpo-certs-col--check" style={{ width: '50px' }} />
              {isPhoto ? (
                <>
                  <col style={{ width: '26%' }} />
                  <col style={{ width: '18%' }} />
                  <col style={{ width: '22%' }} />
                  <col style={{ width: '14%' }} />
                  <col className="tpo-certs-col tpo-certs-col--ops" style={{ width: '140px' }} />
                </>
              ) : (
                <>
                  <col style={{ width: '32%' }} />
                  <col style={{ width: '12%' }} />
                  <col />
                  <col className="tpo-certs-col tpo-certs-col--ops" style={{ width: '140px' }} />
                </>
              )}
            </colgroup>
            <thead>
              <tr>
                <th
                  scope="col"
                  className="tpo-certs-th tpo-certs-th--check"
                  onClick={() => {
                    if (!shopId || loading || selectableKeys.length === 0) return;
                    toggleAll();
                  }}
                >
                  <input
                    type="checkbox"
                    aria-label={t(isPhoto ? 'compliance.lbSelectAllPhoto' : 'compliance.lbSelectAllLabel')}
                    disabled={!shopId || loading || selectableKeys.length === 0}
                    checked={allSelected}
                    onClick={(e) => e.stopPropagation()}
                    onChange={toggleAll}
                  />
                </th>
                {isPhoto ? (
                  <>
                    <th className="tpo-certs-th">{t('orderList.productInfo')}</th>
                    <th className="tpo-certs-th">{t('compliance.lbProjectType')}</th>
                    <th className="tpo-certs-th">{t('compliance.lbPhotoProject')}</th>
                    <th className="tpo-certs-th">{t('compliance.lbRecogStatus')}</th>
                  </>
                ) : (
                  <>
                    <th className="tpo-certs-th">{t('orderList.productInfo')}</th>
                    <th className="tpo-certs-th tpo-certs-th--shelf">{t('compliance.lbColStatus')}</th>
                    <th className="tpo-certs-th">{t('compliance.lbNote')}</th>
                  </>
                )}
                <th className="tpo-certs-th tpo-certs-th--ops">{t('certLib.colOps')}</th>
              </tr>
            </thead>
            <tbody>
              {loading || rows.length === 0 ? (
                <tr>
                  <td colSpan={colSpan} className="tpo-certs-empty-cell">
                    <div className="tpo-certs-empty">
                      <p className="tpo-certs-empty__title">
                        {loading ? t('returnList.loading') : (isPhoto ? t('compliance.lbEmptyPhoto') : t('compliance.lbEmptyLabel'))}
                      </p>
                      {loading ? null : (
                        <p className="tpo-certs-empty__desc">{emptyMessage}</p>
                      )}
                    </div>
                  </td>
                </tr>
              ) : rows.map((row, index) => {
                const skc = pickField(row, 'skc', 'skcName') || `row-${index}`;
                const meta = productMeta[skc] || {};
                const rowTitle = meta.title || pickField(row, 'title', 'productTitle') || '';
                const rowImage = meta.imageUrl || pickField(row, 'imageUrl', 'mainImageUrl') || '';
                const spuValue = meta.spuName
                  || pickField(row, 'spu', 'spuName', 'productId')
                  || skc;
                const productInfoCell = (
                  <td className="tpo-certs-td tpo-certs-td--product">
                    <div className="tpo-photo-product">
                      {rowImage ? (
                        <TemuProductThumb
                          imageUrl={rowImage}
                          className="tpo-photo-product__img"
                          placeholderClassName="tpo-photo-product__img tpo-photo-product__img--empty"
                          alt={rowTitle || (isPhoto ? spuValue : skc)}
                        />
                      ) : (
                        <div className="tpo-photo-product__img tpo-photo-product__img--empty" />
                      )}
                      <div className="tpo-photo-product__meta">
                        <HoverCopyField
                          value={rowTitle}
                          className="tpo-photo-product__title"
                          popoverCopy
                        />
                        {isPhoto ? (
                          <HoverCopyField
                            label="SPU"
                            value={spuValue}
                            className="tpo-photo-product__skc"
                            separator=": "
                          />
                        ) : (
                          <>
                            {meta.spuName ? (
                              <HoverCopyField
                                label="SPU"
                                value={meta.spuName}
                                className="tpo-photo-product__skc"
                                separator=": "
                              />
                            ) : null}
                            <HoverCopyField
                              label="SKC"
                              value={skc}
                              className="tpo-photo-product__skc"
                              separator=": "
                            />
                          </>
                        )}
                        {meta.supplierCode ? (
                          <HoverCopyField
                            label={t('tiktokCatalog.supplier')}
                            value={meta.supplierCode}
                            className="tpo-photo-product__skc"
                            separator=": "
                          />
                        ) : null}
                        <div
                          className="tpo-photo-product__category tpo-muted"
                          title={meta.categoryPathLabel || meta.productTypeId || undefined}
                        >
                          {t('compliance.lbCategory', { name: meta.categoryPathLabel || meta.productTypeId || '—' })}
                        </div>
                      </div>
                    </div>
                  </td>
                );
                const checkCell = (
                  <td
                    className="tpo-certs-td tpo-certs-td--check"
                    onClick={() => toggleOne(skc)}
                  >
                    <input
                      type="checkbox"
                      aria-label={t('orderList.selectRow', { title: skc })}
                      checked={selectedKeys.has(skc)}
                      onClick={(e) => e.stopPropagation()}
                      onChange={() => toggleOne(skc)}
                    />
                  </td>
                );
                if (!isPhoto) {
                  return (
                    <tr key={skc}>
                      {checkCell}
                      {productInfoCell}
                      <td className="tpo-certs-td tpo-certs-td--shelf">
                        <span className={`tpo-records__status tpo-records__status--${shelfTone(row)}`}>
                          {shelfLabel(row)}
                        </span>
                      </td>
                      <td className="tpo-certs-td tpo-muted">{t('compliance.lbPrintHint')}</td>
                      <td className="tpo-certs-td tpo-certs-td--ops">
                        <button
                          type="button"
                          className="tpo-btn tpo-btn--primary tpo-btn--sm"
                          onClick={() => setPrintSkc(skc)}
                        >
                          {t('compliance.lbPrintLabels')}
                        </button>
                      </td>
                    </tr>
                  );
                }
                const materialStatus = photoMaterialStatusOf(row);
                const projectTypeItems = complianceProjectTypeItems(row);
                const contentItems = labelContentItems(row);
                const canView = hasUploadedPhoto(row);
                const canFailReason = hasRejectFailReason(row);
                const autoBindError = String(autoBindErrors[skc] || '').trim();
                return (
                  <tr key={skc}>
                    {checkCell}
                    {productInfoCell}
                    <td className="tpo-certs-td tpo-certs-td--wrap">
                      <CellStack items={projectTypeItems} />
                    </td>
                    <td className="tpo-certs-td tpo-certs-td--wrap">
                      <CellStack items={contentItems} />
                    </td>
                    <td className="tpo-certs-td">
                      <span className={`tpo-status tpo-status--${materialStatus.tone}`}>
                        <span className={`tpo-compliance-info-list__dot tpo-compliance-info-list__dot--${
                          materialStatus.tone === 'success' ? 'ok'
                            : materialStatus.tone === 'failed' ? 'bad'
                              : 'pending'
                        }`}
                        />
                        {materialStatus.label}
                      </span>
                    </td>
                    <td className="tpo-certs-td tpo-certs-td--ops">
                      <div className="tpo-drafts__actions">
                        <button
                          type="button"
                          className="tpo-btn tpo-btn--primary tpo-btn--sm"
                          onClick={() => setBindSkc(skc)}
                        >
                          {t('compliance.lbModify')}
                        </button>
                        {canView ? (
                          <button
                            type="button"
                            className="tpo-btn tpo-btn--secondary tpo-btn--sm"
                            onClick={() => setViewRow(row)}
                          >
                            {t('compliance.lbView')}
                          </button>
                        ) : null}
                        {canFailReason ? (
                          <button
                            type="button"
                            className="tpo-btn tpo-btn--secondary tpo-btn--sm"
                            onClick={() => setFailRow(row)}
                          >
                            {t('compliance.lbFailReason')}
                          </button>
                        ) : null}
                        {autoBindError ? (
                          <button
                            type="button"
                            className="tpo-btn tpo-btn--secondary tpo-btn--sm"
                            onClick={() => setAutoBindErrorSkc(skc)}
                          >
                            {t('compliance.lbErrorLog')}
                          </button>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <TemuOpsPager
          page={page}
          pageSize={pageSize}
          total={totalSkc}
          loading={loading}
          disabled={!shopId}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setPage(1);
          }}
          ariaLabel={t(isPhoto ? 'compliance.lbPhotoPager' : 'compliance.lbLabelPager')}
        />
      </ProductListCard>

      <BatchQueryModal
        open={batchOpen}
        title={t('tiktokCatalog.batchInput', { label: isPhoto ? idTypeLabel : 'SKC' })}
        initialValue={skcDraft}
        onClose={() => setBatchOpen(false)}
        onConfirm={(tokens) => {
          setSkcDraft(joinBatchTokens(tokens));
        }}
      />
      {isPhoto ? (
        <>
          <LabelBindModal
            open={Boolean(bindSkc)}
            shopId={shopId}
            skc={bindSkc}
            productMeta={bindSkc ? productMeta[bindSkc] : null}
            onClose={() => setBindSkc('')}
            onSaved={() => {
              setBindSkc('');
              load({ refreshPool: true });
            }}
          />
          <LabelViewModal
            open={Boolean(viewRow)}
            row={viewRow}
            onClose={() => setViewRow(null)}
          />
          <FailReasonModal
            open={Boolean(failRow)}
            row={failRow}
            onClose={() => setFailRow(null)}
          />
          <AutoBindErrorModal
            open={Boolean(autoBindErrorSkc)}
            skc={autoBindErrorSkc}
            reason={autoBindErrors[autoBindErrorSkc] || ''}
            onClose={() => setAutoBindErrorSkc('')}
          />
        </>
      ) : (
        <LabelPrintModal
          open={Boolean(printSkc)}
          shopId={shopId}
          skc={printSkc}
          onClose={() => setPrintSkc('')}
        />
      )}

      {isPhoto ? (
        <ComplianceAutoBindRuleModal
          open={autoBindModalOpen}
          shopId={shopId}
          kind="photo"
          busy={syncing || loading || seedingDemo || deleting}
          onClose={() => setAutoBindModalOpen(false)}
          onRan={async () => {
            const prefRes = await temuPlatformAPI.getPreferences().catch(() => null);
            const prefs = prefRes?.preferences || prefRes || {};
            setAutoBindErrors(readComplianceAutoBindErrors(prefs, shopId, 'photo'));
            load({ refreshPool: true }).catch(() => { });
          }}
        />
      ) : null}
      <SyncPlatformJsonModal
        open={platformJsonOpen}
        shopId={shopId}
        dumpKind="compliance"
        title={isPhoto ? t('compliance.lbJsonPhoto') : t('compliance.lbJsonLabel')}
        hint={t('compliance.lbJsonHint')}
        onClose={() => setPlatformJsonOpen(false)}
      />
    </>
  );
}
